import {
  angleModeFor, addVersion, applyOptionsVersion, branchSetsToCheck, removeVersion, versionRollKey, writeOptionsVersion, visibleTargeted, roleForMode, rolesForMode, displayEvaluation, branchesUnreachableInRange, keywordReachability, checkBranchCoverage, enumerateFormulaValues, formulaBoundsCheap, formulaComboCount, evaluateEquation, evaluateKeyword, keywordBranchRoleIssue, normalizeKeyword, parseThreshold, resolveEffectiveOptions, resolveMatchedBranch, TRIG_CALL_PATTERN, activeFeedbackForBranch, pickScenarioBranch, resolveScenarioOptions, unreviewedCount, drawnScenarioId , FREE_TEXT_ANSWER_MAX_CHARS } from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, GripVertical, Minus, TextCursor, Trash2, X } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useArmedDelete } from './block-actions';
import { type BlockIssue, findDuplicateGroups, type IssueCache, type IssueContext, issueFieldIds, IssuesPanel, memoizedByKey, useIssueContext } from './block-issues';
import { BlockQuestionEditor } from './block-question-editor';
import { BranchConditionCarousel } from './branch-condition-carousel';
import { requestBranchJump, useBranchJumpTarget } from './branch-jump';
import { formatCoverageList, minSampleValues } from './branch-setup';
import { BranchingCorrectsRow } from './branching-corrects-row';
import { CollapsedInputBlock, collapsedModeBar, collapsedIssueBar, type TestBarChip, type TestBarProps } from './collapsed-input-block';
import { ConditionChipRow, toTestBarChips, type ConditionChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { chipToneClass } from './eval-chip';
import { FeedbackBranchPager } from './feedback-branch-pager';
import { FeedbackCard, ChipDivider, TRIGGER_CHIP_CLASS, TRIGGER_CHIP_TEXT_CLASS, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { gradingModes } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { OptionsSection } from './options-section';
import { DrivenRowFrame, ScenarioSetupFrame } from './scenario-row-frame';
import { StrandWarning, useArmSettle } from './strand-warning';
import { TriggerDropdown } from './trigger-dropdown';
import { useBlockVersions, useTestResetOnVersionChange, useVersionSeeding, versionStructure } from './use-block-versions';
import { useDrivenRowDeleteWarning } from './use-driven-row-dependents';
import { useDrivenVersionProjection } from './use-driven-versions';
import { useInputBlockFeedback, hasEmptyFeedbackContent, normalizeFeedback, positiveTriggerForMode, resetContentIfCustomized, retriggerPositiveSlot } from './use-input-block-feedback';
import { makeRowBranch, nestedBranching, type SeedMode } from './use-nested-branching';
import { useRestoredData } from './use-restored-data';
import { useScenarioFollower } from './use-scenario-follower';
import { useDelayedStickyError, useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { BlockQuestion, AnswerDisplayFrame, hasRollableVariables, htmlHasVariableChips, processVariableChipHTML, rollVariableValues, variableChipNames } from './variable-chip-preview';
import { QuestionHeadingLink, VariesWithSection } from './varies-with-section';
import { VersionBranchNotice } from './version-branch-notice';
import { VersionSelector } from './version-selector';

import type { Block, TaskVariable } from '@/types/experience';
import type { AngleMode, ConditionalRoleBranch, KeywordBlockData, KeywordFeedback, KeywordOption, FeedbackState, RoleVersion, ScenarioRoleBranch } from '@agavi/blocks';
import type { Editor } from '@tiptap/core';

import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_BLEED, ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import { TextField } from '@/components/ui/text-field';
import { useBlockAngleModes } from '@/contexts/angle-mode-context';
import { useTaskVariables, useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { catchAllTitle, legendStatesFor, catchAllChips, KEYWORD_UNLISTED, keywordChips, keywordOptionLabelText } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { settingAvailable } from '@/lib/mode-gated-settings';

/** Bridges "jump to this branch" from the live-test widget to the editor — see
 * DropdownBlock's identical `requestBranchJump`/`branchJumpListeners`/`pendingBranchJump` for the
 * full reasoning (two separate mounted instances of this component, no other channel to hand off
 * a value like this). Deliberately NOT automatic on every reroll — only fires when the teacher
 * explicitly clicks the jump action. */
// ─── Types ────────────────────────────────────────────────────────────────────

interface KeywordBlockProps {
  block: Block;
  isPreview: boolean;
  /** Bumped once per click of Preview's single bottom Submit — see task-content.tsx's own doc
   * comment. Only meaningful while `isPreview`; unused otherwise. */
  previewSubmitSignal?: number;
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  initialIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (isEditing: boolean) => void;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

interface TriggerOption {
  value: string;
  /** Positional, never blank ("Keyword 2") — picker MENU rows need a stable identifier so blank
   * keywords stay distinguishable and selectable in the list. Never shown outside the menu. */
  label: string;
  /** The keyword's raw text, genuinely blank when the keyword itself is — used for every
   * DISPLAY-facing surface (the trigger button's own selection, the summary chip) instead of
   * `label`, so a blank keyword reads as obviously incomplete there rather than like real,
   * already-authored content. */
  text: string;
  evaluation?: 'correct' | 'incorrect' | 'neutral';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** A new option, for an event handler. NEVER call this during render — see DEFAULT_OPTIONS. */
function makeOption(): KeywordOption {
  return { id: crypto.randomUUID(), text: '', role: 'neutral' };
}

/** The starter option, with a FIXED id — see DEFAULT_OPTIONS in mcq-block for the full reasoning. */
const DEFAULT_OPTIONS: readonly KeywordOption[] = [
  { id: 'default-option-1', text: '', role: 'correct' },
];

/** Human-readable label for a single keyword — its own text, or blank when the keyword itself
 * is blank. Deliberately does NOT fall back to a positional "Keyword 2" placeholder: that would
 * read as real, already-authored content and hide the fact that the keyword needs to be filled
 * in — an obviously-incomplete label (nothing) is the honest signal, matching the same
 * blank-stays-blank treatment MCQ/Dropdown use for their own option labels. */
/** Derived in `lib/` now, so Flow and any other surface read the same function this writes with. */
const keywordOptionLabel = keywordOptionLabelText;

/** The mechanically-derived, non-editable title for a targeted feedback state — always
 * reflects exactly which keyword it targets, so two feedbacks can only collide on title when
 * they genuinely target the same keyword (a real setup error), never by coincidence. */
function keywordTargetedTitle(option: KeywordOption, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return `${t('blocks.answers.selectedPrefix')}${keywordOptionLabel(option)}`;
}

/** Re-derives every targeted feedback title against the current options array — needed
 * whenever a keyword's text changes, since every keyword's title embeds its current text. */
function resyncKeywordTargetedTitles(options: KeywordOption[], states: FeedbackState[], t: (key: string, opts?: Record<string, unknown>) => string): FeedbackState[] {
  return states.map(s => {
    if (s.type !== 'targeted' || !s.trigger.startsWith('option_')) return s;
    const id = s.trigger.slice('option_'.length);
    const idx = options.findIndex(o => o.id === id);
    if (idx === -1) return s;
    return { ...s, title: keywordTargetedTitle(options[idx]!, t) };
  });
}

/** Targeted feedback's evaluation always mirrors its target keyword's role — keeps the two
 * from silently drifting apart, same fix as Dropdown's `resyncDropdownTargetedEvaluations`.
 *
 * Includes `role === 'correct'` — this state becomes functionally unreachable the instant its
 * own keyword is correct (correct-first-terminal means it's never even consulted), so the actual
 * evaluation value stops mattering at runtime either way, but it must still update: Flow's
 * branch-collapsing (flow-utils.ts's feedbackStatesOf) relies on evaluation === 'correct' to
 * detect "this branch's copy is dead, exclude it from this condition's scope" — leaving it stale
 * here silently broke that detection, letting a targeted state for a now-correct keyword keep
 * showing up as a live Flow condition. The block's own editor was never affected by the staleness
 * (isTargetedStaleForActive already determines visibility via a direct role lookup, not this
 * field), which is exactly why this went unnoticed until Flow started trusting evaluation. */
function resyncKeywordTargetedEvaluations(options: KeywordOption[], states: FeedbackState[]): FeedbackState[] {
  return states.map(s => {
    if (s.type !== 'targeted' || !s.trigger.startsWith('option_')) return s;
    const id = s.trigger.slice('option_'.length);
    const opt = options.find(o => o.id === id);
    if (!opt) return s;
    return { ...s, evaluation: opt.role };
  });
}// ─── Unified issue reporting ────────────────────────────────────────────────────
// One computation, read by every surface (collapsed bar, test-panel diagnostics, in-editor
// banner) instead of each maintaining its own similar-but-not-identical checks — mirrors
// dropdownIssues/mcqIssues exactly, minus optionsAsEquations (never applies to Keyword —
// see the port plan) and minus a 'multiple-correct' branch-role case (multiple correct keywords
// per branch is a normal, intended pattern here, not an error).

/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `keywordIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function keywordBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return keywordIssues(buildNormalizedKeywordData(initData(block.data), ctx.t), ctx);
}

export function keywordIssues(d: KeywordBlockData, ctx: IssueContext): BlockIssue[] {
  const { t, variables } = ctx;
  const issues: BlockIssue[] = [];
  const categoryQuestion = t('blocks.areas.question');
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  /* UNREVIEWED ROWS, counted — the strip inside the box only speaks for the row you are on, so
     a teacher who never pages to scenario 5 never learns that scenario 5 was never decided. */
  const unreviewedRows = unreviewedCount(d.scenarioBranches);
  if (unreviewedRows > 0) {
    issues.push({
      key: 'scenarios-unreviewed', severity: 'warning', kind: 'incompletion',
      category: categoryAnswerSetup,
      detail: t('blocks.scenario.unreviewedScenarios', { count: unreviewedRows }),
    });
  }
  const categoryAlternativeCorrectStates = t('blocks.areas.alternativeAnswerSetup');
  const categoryFeedback = t('blocks.areas.feedback');
  const optionIsBlank = (o: KeywordOption) => !o.text.trim();
  const questionBlank = !d.question.replace(/<\/?p>/g, '').trim();
  const allOptionsBlank = d.options.every(optionIsBlank);

  if (questionBlank) issues.push({ key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true, category: categoryQuestion, detail: t('blocks.issues.questionBlank') });
  if (allOptionsBlank) {
    issues.push({ key: 'options-blank', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.keyword.allOptionsBlankWarning') });
  } else if (d.gradingMode === 'graded') {
    if (d.conditionalBranches && d.conditionalBranches.length > 0) {
      // A keyword marked correct in this branch but left with blank text is a dead entry — the
      // same "blank text can't count as configured" bug the static check below guards against.
      // Checks every correct-marked option, not just the first found, since (unlike Dropdown's
      // single-correct radio invariant) Keyword allows more than one correct keyword per branch.
      d.conditionalBranches.forEach((b, i) => {
        const blankCorrectOption = d.options.find(o => b.roles[o.id] === 'correct' && optionIsBlank(o));
        if (blankCorrectOption) {
          issues.push({ key: `branch-${b.id}-correct-blank`, severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.keyword.diagnosticBranchCorrectBlank', { number: i + 1 }) });
        }
      });
    } else {
      // Unlike Dropdown (always exactly one correct option, radio invariant), Keyword can have
      // zero correct keywords marked at all, OR one or more marked correct but every one of them
      // left blank — two distinct problems, same split as MCQ/Dropdown's own no-correct-option
      // vs correct-option-blank messages.
      const correctOptions = d.options.filter(o => o.role === 'correct');
      if (correctOptions.length === 0) {
        issues.push({ key: 'no-correct-option', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.answers.noCorrectAnswers') });
      } else if (correctOptions.every(optionIsBlank)) {
        issues.push({ key: 'correct-option-blank', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.keyword.correctOptionBlankWarning') });
      }
    }
  }

  // Duplicate keyword text is a real evaluation bug, not just clutter — matching finds the FIRST
  // option whose NORMALIZED text matches, so a second keyword that normalizes to the same
  // comparison key (even a different role) is silently unreachable. Keyed on normalizeKeyword
  // using this block's OWN caseSensitive/accentSensitive settings — not a fixed exact-string
  // rule — since whether "cafe" and "Café" actually collide at evaluation time depends entirely
  // on those toggles. A byte-identical pair always collides regardless of settings, so this is a
  // strict superset of a plain exact-string check.
  for (const group of findDuplicateGroups(d.options, o => normalizeKeyword(o.text, d.caseSensitive, d.accentSensitive))) {
    const uniqueTexts = [...new Set(group.map(o => o.text.trim()))];
    issues.push({
      key: `duplicate-${group.map(o => o.id).join('-')}`,
      fields: group.map(o => o.id),
      severity: 'error',
      kind: 'defect',
      category: categoryAnswerSetup,
      detail: uniqueTexts.length > 1
        ? t('blocks.keyword.duplicateKeywordNormalizedWarning', { texts: uniqueTexts.map(x => `"${x}"`).join(', ') })
        : t('blocks.keyword.duplicateKeywordWarning', { keyword: uniqueTexts[0] }),
    });
  }

  // Everything below requires an explicit action (turning on branching, typing an equation,
  // marking a role) — see dropdownIssues' own comment for why none of this needs the grace
  // period, and why it's NOT gated on `!allOptionsBlank` either.
  /* PER VERSION: these checks read version-resolved data, so a banked block was only ever
     validated for the version being displayed and a gap authored in version 3 stayed invisible.
     See `branchSetsToCheck`. */
  if (d.gradingMode === 'graded') {
    for (const set of branchSetsToCheck<ConditionalRoleBranch>(d)) {
      const branches = set.branches ?? [];
      if (branches.length === 0) continue;
      /** Keys must differ per set, or two sets' gaps collapse into one issue. */
      const vkey = (k: string) => (set.versionIndex !== null ? `v${set.versionIndex}-${k}`
        : set.scenarioIndex !== null ? `s${set.scenarioIndex}-${k}` : k);
      /** And the message must say WHICH one, or it names a condition nobody can find. */
      const vlabel = (detail: string) => (set.versionIndex !== null
        ? t('blocks.branching.versionScopedIssue', { number: set.versionIndex, detail })
        : set.scenarioIndex !== null
          ? t('blocks.branching.scenarioScopedIssue', { number: set.scenarioIndex, detail })
          : detail);
      const equationTrimmed = set.equation?.trim();
      if (!equationTrimmed) {
        issues.push({ key: vkey('no-equation'), severity: 'error', kind: 'incompletion', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.diagnosticNoEquation')) });
      } else if (evaluateEquation(equationTrimmed, minSampleValues(variables), angleModeFor(d, 'conditionalEquation')) === null) {
        issues.push({ key: vkey('invalid-equation'), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.equationInvalidWarning')) });
      } else {
        // The domain narrows coverage to the values the equation can actually produce when it is a
        // bare scenario-selector variable; null everywhere else, which keeps the continuous check.
        const domainValues = enumerateFormulaValues(equationTrimmed, variables);
        // Too many values to build the set, but still a discrete variable. The real-line gap
        // analysis would then name regions it can never produce ("<0, >0" for a 1-to-100
        // variable), so gaps are suppressed and the teacher is told coverage went unchecked.
        const comboCount = domainValues ? null : formulaComboCount(equationTrimmed, variables);
        const discreteButUnchecked = comboCount !== null;
        const coverage = checkBranchCoverage(branches, domainValues);
        const gapIssues = coverage.issues.filter(i => i.matchingBranchIds.length === 0);
        const overlapIssues = coverage.issues.filter(i => i.matchingBranchIds.length > 0);
        if (gapIssues.length > 0 && !discreteButUnchecked) {
          issues.push({ key: vkey('coverage-gap'), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.missingCoverage', { list: formatCoverageList(gapIssues, t, 'gap') })) });
        }
        if (overlapIssues.length > 0 && !discreteButUnchecked) {
          issues.push({ key: vkey('coverage-overlap'), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.overlappingConditions', { list: formatCoverageList(overlapIssues, t, 'overlap') })) });
        }
        if (discreteButUnchecked) {
          issues.push({ key: vkey('coverage-unchecked'), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
            detail: vlabel(t('blocks.branching.coverageUnchecked', { count: comboCount })) });
        }
        // Reachability survives a domain too large to enumerate: bounds alone prove a threshold
        // outside them can never be met, and for a bare variable the bounds are its own min and max.
        const cheapBounds = domainValues ? null : formulaBoundsCheap(equationTrimmed, variables, angleModeFor(d, 'conditionalEquation'));
        if (cheapBounds) {
          branchesUnreachableInRange(branches, cheapBounds.lo, cheapBounds.hi).forEach(id => {
            const number = (branches).findIndex(b2 => b2.id === id) + 1;
            if (number === 0) return;
            issues.push({ key: vkey(`branch-${id}-unreachable`), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
              detail: vlabel(t('blocks.branching.branchUnreachable', { number, lo: cheapBounds.lo, hi: cheapBounds.hi })) });
          });
        }
        // A condition no attainable value can satisfy: its whole answer setup is dead. A WARNING, not
        // an error — nothing a student does can reach it, so it cannot mis-grade anyone, and where it
        // does cause harm the coverage gap beside it already says so at error severity. Matches
        // `feedback-overlaps-correct`, the existing "authored but can never fire" issue.
        if (domainValues && domainValues.length > 0) {
          coverage.unreachableBranchIds.forEach(id => {
            const number = (branches).findIndex(b2 => b2.id === id) + 1;
            if (number === 0) return;
            issues.push({ key: vkey(`branch-${id}-unreachable`), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
              detail: vlabel(t('blocks.branching.branchUnreachable', { number, lo: domainValues[0], hi: domainValues[domainValues.length - 1] })) });
          });
        }
      }
      branches.forEach((b, i) => {
        if (keywordBranchRoleIssue(b, d.options) === 'none-correct') {
          issues.push({ key: vkey(`branch-${b.id}-none`), severity: 'error', kind: 'incompletion', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.keyword.diagnosticBranchNoneCorrect', { number: i + 1 })) });
        }
        if (b.operator === 'between') {
          const lo = parseThreshold(b.threshold);
          const hi = parseThreshold(b.thresholdHigh);
          if (Number.isFinite(lo) && Number.isFinite(hi) && lo > hi) {
            issues.push({ key: vkey(`branch-${b.id}-inverted`), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.diagnosticBranchInverted', { number: i + 1 })) });
          }
        }
      });
    }
  }

  // Validation asks the SAME question grading does. A targeted state in the top-level set can never
  // fire once branches are configured — `activeFeedbackForBranch` strips them in the unresolved
  // fallback — so a blank one is unreachable by construction and reporting it sent a teacher
  // looking for something the editor cannot even show them.
  const hasMissingFeedbackText = hasEmptyFeedbackContent(activeFeedbackForBranch(undefined, d.feedback, (d.conditionalBranches?.length ?? 0) > 0).states) ||
    !!d.conditionalBranches?.some(b => hasEmptyFeedbackContent(b.feedback.states));
  if (hasMissingFeedbackText) issues.push({ key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.feedbackPanel.missingText') });

  return issues;
}

function initData(raw: Record<string, unknown>): KeywordBlockData {
  type RawOption = { id: string; text: string; role?: KeywordOption['role'] };
  const opts = raw.options as RawOption[] | undefined;
  const feedback = (raw.feedback ?? { states: [] }) as KeywordBlockData['feedback'];
  // Branch-owned feedback (see ConditionalRoleBranch in @agavi/blocks) is newer than
  // conditionalBranches itself — see Dropdown's identical backfill for the full reasoning
  // (a branch saved before that field existed has no `.feedback` at all; reading `.feedback.states`
  // off one of those unconditionally would crash the whole render tree).
  type RawBranch = Omit<ConditionalRoleBranch, 'feedback'> & { feedback?: ConditionalRoleBranch['feedback'] };
  const rawBranches = raw.conditionalBranches as RawBranch[] | undefined;
  const conditionalBranches: ConditionalRoleBranch[] | undefined = rawBranches?.map(b => ({
    ...b,
    // Deterministic, not `crypto.randomUUID()`. `initData` runs during render, so minting here
    // gave every render a fresh set of state ids for any branch that has not authored its own
    // feedback — and a feedback state's id is what `firedIds`, Flow rules and targeted triggers
    // all match on. Same fault that made a fresh MCQ grade its own correct answer wrong.
    feedback: b.feedback ?? { states: feedback.states.map(s => ({ ...s, id: `${b.id}-${s.id}` })) },
  }));
  return {
    question: (raw.question as string) ?? '',
    options: opts?.length
      ? opts.map(o => ({ id: o.id, text: o.text, role: o.role ?? 'neutral' }))
      : DEFAULT_OPTIONS.map(o => ({ ...o })),
    caseSensitive: (raw.caseSensitive as boolean) ?? false,
    accentSensitive: (raw.accentSensitive as boolean) ?? true,
    gradingMode: (['graded', 'survey', 'optional'].includes(raw.gradingMode as string)
      ? raw.gradingMode as KeywordBlockData['gradingMode']
      : 'graded'),
    feedback,
    conditionalEquation: raw.conditionalEquation as KeywordBlockData['conditionalEquation'],
    conditionalBranches,
    conditionalBranchesDraft: raw.conditionalBranchesDraft as KeywordBlockData['conditionalBranchesDraft'],
    angleMode: (['degrees', 'radians'].includes(raw.angleMode as string) ? raw.angleMode as AngleMode : 'degrees'),
    angleModes: (raw.angleModes as Record<string, AngleMode> | undefined),
    /* Same hazard as `versions` below: a scenario link read back as absent is a block that
       silently stops following, with its per-scenario answers orphaned behind it. */
    followsBlockId: raw.followsBlockId as KeywordBlockData['followsBlockId'],
    /* And the driven link, by the same rule and for the same reason. */
    drivenBy: raw.drivenBy as KeywordBlockData['drivenBy'],
    scenarioBranches: raw.scenarioBranches as KeywordBlockData['scenarioBranches'],
    /* Builds a NEW object field by field, so anything not named here is dropped — which is how a
       bank comes to be created, written to Mongo and then silently gone the moment the block
       re-reads itself. Pinned for MCQ by `mcq-versions.test.tsx`. */
    versions: raw.versions as KeywordBlockData['versions'],
  };
}

/** Backfills default feedback text/titles for a block read straight from `block.data` — mirrors
 * buildNormalizedDropdownData exactly (see its own doc comment for the full reasoning), shared by
 * both non-editing render paths (the collapsed/isSelected test widget and the student-facing
 * Preview) so they can never drift into two different sets of defaults. */
/**
 * Normalised block data, with the drawn question VERSION already in force.
 *
 * Version first, then normalise: a version carries its own feedback, and it has to go
 * through the same defaulting the block's own does or a versioned block would miss the
 * titles a plain one gets. `applyOptionsVersion` is identity when there are no versions, so
 * this is unchanged for every block that has never had one authored.
 */
function buildNormalizedKeywordData(
  rawD: KeywordBlockData,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /** The version drawn for this student, from the session roll. */
  versionId?: string,
): KeywordBlockData {
  const rawVersioned = applyOptionsVersion(rawD, versionId);
  return {
    ...rawVersioned,
    feedback: normalizeFeedback(
      rawVersioned.feedback,
      {
        unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
        unansweredContent: `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>`,
        incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
        incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
        correctTitle: rawVersioned.gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle')
          : rawVersioned.gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle')
          : t('blocks.feedbackDefaults.anyResponseTitle'),
        correctContent: rawVersioned.gradingMode === 'graded' ? `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`
          : rawVersioned.gradingMode === 'survey' ? `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`
          : `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
      },
      [],
      positiveTriggerForMode(rawVersioned.gradingMode),
    ),
  };
}

/** This block's fired feedback — see `FiredFeedback`, which every block now shares. */
function renderKeywordFiredFeedback(testResult: FeedbackState[] | null, rolledValues: Record<string, string>): React.ReactNode {
  return <FiredFeedback states={testResult} rolledValues={rolledValues} />;
}

/** The one canonical "what a student sees" layout — question, then a centered free-text answer
 * input — shared by every place that renders a student-facing view of this block (currently the
 * teacher-facing Preview and the editor's own live-test widget; the eventual student interface
 * should reuse this same shape once it consumes @agavi/blocks, rather than re-deriving the layout
 * independently). Mirrors DropdownAnswerDisplay's own doc comment: deliberately owns no outer
 * card/border chrome; `children` is the fired-feedback display, not part of the canonical shape
 * itself. */
function KeywordAnswerDisplay({
  question, rolledValues, value, onChange, disabled, children, scenario,
}: {
  question: string;
  /** The leader's variant, ghosted, above the question — see `AnswerDisplayFrame`. */
  scenario?: React.ReactNode;
  rolledValues: Record<string, string>;
  value: string;
  onChange: (text: string) => void;
  disabled?: boolean;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <AnswerDisplayFrame question={question} rolledValues={rolledValues} scenario={scenario}>
      <TextField
        variant='prose'
        value={value}
        onChange={onChange}
        disabled={disabled}
        // Name and placeholder are deliberately DIFFERENT strings, though they were one key.
        // A placeholder instructs ("Type your answer"); a name identifies, and has to still say
        // which box this is once the student has typed and the placeholder is gone. "Keyword"
        // rather than "Short answer" because it is the precise word: one word is wanted, and
        // "short answer" invites a phrase.
        ariaLabel={t('blocks.keyword.answerAria')}
        placeholder={t('blocks.keyword.answerPlaceholder')}
        // `mx-auto md:max-w-xs`, matching DropdownAnswerDisplay's select exactly — full width on a
        // phone, capped and centred above that. The cap was dropped in the TextField migration,
        // leaving `mx-auto` on a full-width element, which centres nothing. The collapsed card's
        // read-only version kept its own copy, so the two disagreed about the same layout.
        // Capped on BOTH sides of the match — see FREE_TEXT_ANSWER_MAX_CHARS. Refuses past the
        // limit rather than truncating, so the student sees their answer stop rather than
        // discovering later that its tail was eaten.
        maxLength={FREE_TEXT_ANSWER_MAX_CHARS}
        className='mx-auto md:max-w-xs' />
      {children}
    </AnswerDisplayFrame>
  );
}

interface KeywordTestData {
  resolvedVariables: Record<string, number>;
  effectiveOptions: KeywordOption[];
  matchedBranch: ConditionalRoleBranch | null;
  matchedBranchIndex: number;
  matchedEquationValue: number | null;
  testResult: FeedbackState[] | null;
  resolvedD: KeywordBlockData;
  testChips: TestBarChip[];
  hasChips: boolean;
  hasRollable: boolean;
}

function computeKeywordTestData(
  d: KeywordBlockData,
  variables: TaskVariable[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  rolledValues: Record<string, string>,
  submittedAnswer: string,
  hasSubmitted: boolean,
  /** The leader's variant in force — see `evaluateDropdown`'s own note. */
  scenarioId?: string,
): KeywordTestData {
  const resolvedVariables: Record<string, number> = Object.fromEntries(
    Object.entries(rolledValues)
      .map(([name, val]) => [name, Number(val)] as const)
      .filter((entry): entry is [string, number] => !isNaN(entry[1]))
  );
  // Branches are only ever consulted in graded mode (mirrors evaluateKeyword's own gate) —
  // otherwise a leftover branch from a mode switch would still resolve here even though the real
  // evaluation now ignores it.
  // A FOLLOWER resolves against the scenario in force instead of an equation — the same split
  // `evaluateKeyword` makes internally, mirrored because this builds what the teacher SEES.
  const followed = d.gradingMode === 'graded' && !!d.scenarioBranches && d.scenarioBranches.length > 0;
  const scenarioRow = followed ? pickScenarioBranch(d.scenarioBranches!, scenarioId) : null;
  const effectiveOptions = rolesForMode(d.gradingMode, d.gradingMode !== 'graded'
    ? d.options
    : followed
      /* The same arguments the evaluator passes. These two editors do not offer nested
         branching yet, but `evaluateDropdown`/`evaluateKeyword` already resolve it, and an
         editor that omitted them would show a different answer key than the one that grades
         the moment a row carried one. See MCQ, where exactly that shipped. */
      ? resolveScenarioOptions(d.options, d.scenarioBranches, scenarioId, resolvedVariables, angleModeFor(d, 'conditionalEquation'))
      : resolveEffectiveOptions(d.options, d.conditionalEquation, d.conditionalBranches, resolvedVariables, angleModeFor(d, 'conditionalEquation')));
  const matchedBranch = !followed && d.gradingMode === 'graded'
    ? resolveMatchedBranch(d.conditionalEquation, d.conditionalBranches, resolvedVariables, angleModeFor(d, 'conditionalEquation'))
    : null;
  const activeFeedbackForDisplay = scenarioRow ? scenarioRow.feedback : matchedBranch ? matchedBranch.feedback : d.feedback;
  const matchedBranchIndex = matchedBranch ? (d.conditionalBranches ?? []).findIndex(b => b.id === matchedBranch.id) : -1;
  const matchedEquationValue = matchedBranch && d.conditionalEquation ? evaluateEquation(d.conditionalEquation, resolvedVariables, angleModeFor(d, 'conditionalEquation')) : null;
  const testResult = hasSubmitted ? evaluateKeyword({ text: submittedAnswer }, d, resolvedVariables, scenarioId) : null;
  const resolvedD: KeywordBlockData = { ...d, question: processVariableChipHTML(d.question, rolledValues) };
  const firedIds = new Set((testResult ?? []).map(s => s.id));

  // Which keywords are NOT covered by their own targeted/targeted feedback — same computation
  // the feedback-setup display (Default Correct/Incorrect trigger-zone chips, further down in the
  // main component) builds inline for itself; duplicated here rather than shared (matches how
  // MCQ/Dropdown each keep their own inline copy of this same concept, never factored into one
  // cross-file helper). A correct-role keyword is never counted as "covered" even if a stale
  // targeted state still technically targets it (stale states are hidden/ignored elsewhere) —
  // it must always appear in uncoveredCorrect.
  const coveredIds = new Set(
    activeFeedbackForDisplay.states
      .filter(s => s.type === 'targeted' && s.trigger.startsWith('option_'))
      .map(s => s.trigger.slice('option_'.length))
      .filter(id => effectiveOptions.find(o => o.id === id)?.role !== 'correct')
  );
  // `answerable` is the derivation's word for it: correct keywords a student can actually type.
  // The blank exclusion lives there now, tested against `evaluateKeyword` rather than restated.
  const barReach = keywordReachability(d, effectiveOptions);
  const uncoveredCorrect = barReach.answerable.filter(o => !coveredIds.has(o.id));
  const uncoveredIncorrect = effectiveOptions.filter(o => o.role !== 'correct' && !coveredIds.has(o.id));

  // Keyword's own state-condition text — unlike Dropdown/MCQ's `<state>Condition` helper, there's
  // no single selected option to name here; a targeted state's own keyword text IS the condition.
  // `correct` is handled entirely separately below (expanded into one chip per uncovered correct
  // keyword, never joined into a single combined-text chip — mirrors MCQ's own allowMultiple
  // fan-out, since Keyword can likewise have several simultaneously-correct keywords) — excluded
  // from legendStates so it's never also emitted as one joined chip here.
  const stateCondition = (s: FeedbackState): React.ReactNode => {
    if (s.trigger === 'unanswered') return t('blocks.feedbackDefaults.noAnswerTitle');
    if (s.trigger === 'any_answer') return t('blocks.feedbackDefaults.anyAnswerTitle');
    if (s.trigger === 'any_response') return t('blocks.feedbackDefaults.anyResponseTitle');
    if (s.trigger === 'incorrect') return t('blocks.feedbackDefaults.incorrectTitle');
    const optionId = s.trigger.startsWith('option_') ? s.trigger.slice('option_'.length) : null;
    if (optionId == null) return null;
    const opt = effectiveOptions.find(o => o.id === optionId);
    const label = opt ? keywordOptionLabel(opt) : '';
    return label || null;
  };

  const isVisibleTargeted = (s: FeedbackState) => {
    if (s.type !== 'targeted') return false;
    if (!s.trigger.startsWith('option_')) return true;
    const id = s.trigger.slice('option_'.length);
    return effectiveOptions.find(o => o.id === id)?.role !== 'correct';
  };
  const { listed: legendStates, catchAll: participationCatchAll } =
    legendStatesFor(d.gradingMode, activeFeedbackForDisplay.states, isVisibleTargeted);
  const testChips: TestBarChip[] = legendStates.map(s => ({
    id: s.id,
    condition: stateCondition(s),
    evaluation: displayEvaluation(d.gradingMode, s.evaluation),
    active: firedIds.has(s.id),
    category: s.trigger === 'unanswered' ? 'unanswered' as const
      : s.trigger === 'any_answer' ? 'any_answer' as const
      : s.trigger === 'any_response' ? 'any_response' as const
      : 'targeted' as const,
  }));

  const normalizedSubmitted = hasSubmitted ? normalizeKeyword(submittedAnswer, d.caseSensitive, d.accentSensitive) : null;
  const matchedOpt = normalizedSubmitted != null
    ? effectiveOptions.find(o => normalizeKeyword(o.text, d.caseSensitive, d.accentSensitive) === normalizedSubmitted)
    : undefined;

  // The 'correct' trigger, expanded into one chip per uncovered correct keyword instead of a
  // single joined label — several keywords can be correct at once (synonyms), and only ONE of
  // them is ever what the student actually typed, so each keyword gets its own chip, only the
  // matched one shown active. Mirrors MCQ's allowMultiple correct-chip fan-out exactly (see its
  // own doc comment) — don't collapse multiple correct keywords back into one chip.
  const correctState = d.gradingMode === 'graded' ? activeFeedbackForDisplay.states.find(s => s.trigger === 'correct') : undefined;
  if (correctState) {
    const correctFired = firedIds.has(correctState.id);
    // Any ONE of these is a correct answer, which is what the feedback panel's own row says with an
    // "or" between each pair. Without it the legend read as a list of requirements.
    testChips.push(...toTestBarChips(keywordChips('correct', uncoveredCorrect, t), {
      id: correctState.id,
      category: 'correct',
      active: correctFired,
      activeSourceIds: new Set(matchedOpt ? [matchedOpt.id] : []),
      unreachableLabel: t('blocks.feedbackPanel.unreachable'),
      connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
    }));
  }

  // "Uncovered keyword" fan-out — same reasoning as Dropdown/MCQ's own version: a catch-all
  // Default Incorrect state that has no dedicated per-keyword feedback covering some non-correct
  // keyword shows one synthetic chip per uncovered keyword (neutral-role included, styled
  // accordingly), so the legend never hides which specific wrong/flagged picks would actually fall
  // through to the generic message — this is what was missing before (neutral-role keywords with
  // no targeted feedback never got their own chip at all). Unlike MCQ/Dropdown, Default Incorrect
  // can never collapse to a single "unreachable" chip for Keyword: free text always has an implicit
  // "any unlisted word" catch-all beyond the listed keywords, so the generic chip is always ALSO
  // shown, never replaced by the per-keyword fan-out.
  const incorrectAnyState = d.gradingMode === 'graded' ? activeFeedbackForDisplay.states.find(s => s.trigger === 'incorrect') : undefined;
  if (incorrectAnyState) {
    const incorrectAnyFired = firedIds.has(incorrectAnyState.id);
    // The catch-all is one more alternative, and the panel separates it the same way. It lights up
    // only when NO listed keyword matched, which is what the sentinel source id expresses.
    testChips.push(...toTestBarChips(keywordChips('incorrect', uncoveredIncorrect, t), {
      id: incorrectAnyState.id,
      category: 'incorrect',
      active: incorrectAnyFired,
      activeSourceIds: new Set([matchedOpt?.id ?? KEYWORD_UNLISTED]),
      unreachableLabel: t('blocks.feedbackPanel.unreachable'),
      connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
    }));
  }

  /**
   * The catch-all, fanned out exactly as graded's Default Incorrect is just above.
   *
   * It used to arrive through `legendStates` as ONE chip reading "Any Answer", which is the single
   * thing the feedback panel stopped saying when the catch-all learned to name what it still
   * catches. Same derivation as the panel's now, so the two cannot word it differently.
   */
  if (participationCatchAll) {
    testChips.push(...toTestBarChips(
      catchAllChips(d.gradingMode, legendStates.some(s => s.type === 'targeted'), t,
        () => keywordChips('incorrect', uncoveredIncorrect, t, d.gradingMode)), {
        id: participationCatchAll.id,
        category: d.gradingMode === 'optional' ? 'any_response' : 'any_answer',
        active: firedIds.has(participationCatchAll.id),
        unreachableLabel: t('blocks.feedbackPanel.unreachable'),
        connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
      }));
  }
  const hasChips = htmlHasVariableChips(d.question);
  const hasRollable = hasRollableVariables(variables);
  return { resolvedVariables, effectiveOptions, matchedBranch, matchedBranchIndex, matchedEquationValue, testResult, resolvedD, testChips, hasChips, hasRollable };
}

/**
 * Keyword's two default conditions as chip lists — the ONE derivation the feedback panel and the
 * test legend both draw.
 *
 * They were two hand-rolled copies of one fact. The legend built `TestBarChip`s with standard chip
 * styling; the panel built `<span>`s with its own literal classes (`bg-ws-error-bg`, `max-w-24`)
 * for the non-correct chips and `chipClass` for the correct ones — inconsistent even with itself.
 * The TEXT agreed, which is why this never showed up as a bug report, but nothing kept it agreeing.
 *
 * `font-mono` is preserved deliberately: a keyword is a literal string a student types, and the
 * monospace face is what distinguishes "the word `sublimation`" from prose about it.
 *
 * The catch-all stays generic. Keyword's domain is open — a student can type anything — so the
 * complement of the listed words genuinely cannot be enumerated, and "Any Unlisted Word" is the
 * accurate description rather than a shortcut. It is shown IN ADDITION to the uncovered listed
 * words, never instead of them: those fall through to Default Incorrect too, and they can be named.
 */
/** Both moved to `lib/block-condition-chips` — see that file's note on why the derivation has to
 *  live where a non-block surface can reach it. */

// ─── Option row ───────────────────────────────────────────────────────────────

interface SortableOptionRowProps {
  opt: KeywordOption;
  optionCount: number;
  armedOptionId: string | null;
  setArmedOptionId: (id: string | null) => void;
  roleLocked: boolean;
  onRoleClick: (id: string) => void;
  onTextChange: (id: string, text: string) => void;
  onRemove: (id: string) => void;
  /** Keyword text is a plain string, not tiptap HTML — a variable chip can't live inside it, so
   * this input never registers itself AS the active editor. It only needs to CLEAR whichever
   * editor was previously active on focus, so the shared toolbar goes inert while a teacher is
   * typing a keyword — same one-line pattern FormulaField already uses. */
  activeEditorRef: { current: Editor | null };
  setActiveEditor: (editor: Editor | null) => void;
  /** This keyword collides with another one under the block's own case/accent settings. Read from
   *  `issueFieldIds(editorIssues)`, so the ring and the message cannot name different rows. */
  hasDuplicate?: boolean;
}

function SortableOptionRow({ opt, optionCount, roleLocked, armedOptionId, setArmedOptionId, onRoleClick, onTextChange, onRemove, activeEditorRef, setActiveEditor, hasDuplicate }: SortableOptionRowProps) {
  /* Warns when this row is the only thing choosing a version somewhere later —
     see `useDrivenRowDependents`. Undefined for the overwhelming majority. */
  const strandWarning = useDrivenRowDeleteWarning(opt.id);
  /* See `useArmSettle`: the warning below is what moves the button. */
  const armSettled = useArmSettle(armedOptionId === opt.id);
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: opt.id });
  // Same rule as every other error marker: wait for the row to stop changing before the ring first
  // appears, then hold it until the collision is actually gone. Without it the ring flickers on
  // every keystroke that passes through a colliding prefix while the teacher is still typing.
  const showDuplicate = useDelayedStickyError(!!hasDuplicate, opt.text);

  return (
    <div
      ref={setNodeRef}
      data-field-id={opt.id}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className='flex min-w-0 flex-col gap-1'
    >
      <div
        className={`flex min-w-0 items-center gap-2 ${isDragging ? 'opacity-40' : ''} ${
          armedOptionId === opt.id ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''
        }`}
      >
        <div
          {...listeners}
          {...attributes}
          aria-label={t('blocks.keyword.dragOption')}
          className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
        >
          <GripVertical className='h-4 w-4' />
        </div>

        <button
          type='button'
          onClick={() => onRoleClick(opt.id)}
          disabled={roleLocked}
          aria-label={
            opt.role === 'correct'   ? t('blocks.roleCycle.correct')
              : opt.role === 'incorrect' ? t('blocks.roleCycle.incorrect')
              : t('blocks.roleCycle.neutral')
          }
          className='-mx-3 flex shrink-0 items-center justify-center p-3 disabled:cursor-not-allowed disabled:opacity-40'
        >
          <span className={`pointer-events-none flex h-5 w-5 items-center justify-center rounded-sm transition-colors ${
            opt.role === 'correct' ? 'border-2 border-ws-primary bg-ws-primary' :
            opt.role === 'incorrect' ? 'bg-ws-muted/20' :
            'border-2 border-ws-border'
          }`}>
            {opt.role === 'correct' && <Check className='h-3.5 w-3.5 text-ws-btn-on-primary' strokeWidth={3} />}
            {opt.role === 'incorrect' && <X className='h-3.5 w-3.5 text-ws-muted' strokeWidth={3} />}
            {opt.role === 'neutral' && <Minus className='h-3 w-3 text-ws-muted' strokeWidth={2} />}
          </span>
        </button>

        {/* The duplicate ring goes on a WRAPPER, matching dropdown's own row: the panel names the
            problem, the field says WHICH keyword it is. Both members of a colliding pair light up,
            because neither is individually wrong — it is the pair that cannot both be matched. */}
        <div className={`min-w-0 flex-1 rounded ${showDuplicate ? 'ring-1 ring-ws-error-fg' : ''}`}>
          <TextField fieldId={`${opt.id}.text`}
            variant='literal'
            value={opt.text}
            onChange={v => onTextChange(opt.id, v)}
            ariaLabel={t('blocks.keyword.optionPlaceholder')}
            placeholder={t('blocks.keyword.optionPlaceholder')}
            // The answer KEY takes the same cap as the answer. Matching is whole-answer, so a key
            // longer than a student can type is one nobody could ever give.
            maxLength={FREE_TEXT_ANSWER_MAX_CHARS}
            onFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
            className='w-full' />
        </div>

        <IconButton
          type='button'
          variant='destructive'
          size='md'
          isArmed={armedOptionId === opt.id}
          disabled={optionCount <= 1}
          /*
             SAID TWICE, ON PURPOSE: in the armed label for a reader, and in the warning
             beside the button for everyone else. It used to be the label alone, which made
             it invisible — see `StrandWarning` for why that was chosen and why it was
             wrong. `title` stays for a pointer resting on the icon itself. */
          aria-label={armedOptionId === opt.id
            ? [t('blocks.keyword.confirmRemoveOption'), strandWarning].filter(Boolean).join('. ')
            : t('blocks.keyword.removeOption')}
          title={armedOptionId === opt.id ? strandWarning : undefined}
          className='shrink-0'
          onClick={() => {
            if (armedOptionId === opt.id) { onRemove(opt.id); setArmedOptionId(null); }
            else { setArmedOptionId(opt.id); }
          }}
          onPointerLeave={(e) => { if (e.pointerType !== 'touch' && armedOptionId === opt.id && armSettled()) setArmedOptionId(null); }}
          onBlur={() => { if (armedOptionId === opt.id) setArmedOptionId(null); }}
          onKeyDown={(e) => { if (e.key === 'Escape' && armedOptionId === opt.id) { e.stopPropagation(); setArmedOptionId(null); } }}
        >
          <Trash2 className='h-4 w-4' />
        </IconButton>
      </div>

      {/*
        * BELOW THE ROW, not inside it. Beside the fields it squeezed the answer to make room
        * for a squeezed warning, which helps nobody — owner: "squishing the actual answer to
        * put a squished warning is not helpful". The wrapper carries the sortable ref and the
        * field id so the row keeps being draggable and reachable from the issue list; the
        * armed paint stays on the row, which is the thing being deleted.
        */}
      {armedOptionId === opt.id && strandWarning && <StrandWarning text={strandWarning} />}
    </div>
  );
}

// ─── Sortable feedback card ───────────────────────────────────────────────────

interface SortableFeedbackCardProps extends FeedbackCardProps {
  sortId: string;
}

function SortableFeedbackCard({ sortId, ...cardProps }: SortableFeedbackCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}

// ─── Trigger UI ───────────────────────────────────────────────────────────────


function KeywordTriggerChip({ option }: { option: TriggerOption }) {
  const chipColor =
    chipToneClass(option.evaluation ?? 'neutral');
  return (
    <span className={`${TRIGGER_CHIP_CLASS} ${chipColor}`}>
      {option.evaluation === 'incorrect' && <X aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />}
      {option.evaluation === 'correct'   && <Check aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />}
      {(option.evaluation === 'neutral' || !option.evaluation) && <Minus aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={2} />}
      <ChipDivider />
      <span className={`${TRIGGER_CHIP_TEXT_CLASS} font-mono`}>{option.text}</span>
    </span>
  );
}

// Module-level (not per-instance) — see mcq-block.tsx's identical caches for why: even though
// Keyword has no chip-collision-style expensive check today, this same "recomputes on every
// keystroke anywhere in the task, not just this block's own fields" pattern applies to every
// block's collapsed/preview render path identically, so it's fixed uniformly here rather than
// waiting for a future check to make it visible on this block too.
const keywordIssuesCache: IssueCache<BlockIssue[]> = new WeakMap();
const keywordTestDataCache: IssueCache<KeywordTestData> = new WeakMap();
/**
 * A SECOND cache, for the draft.
 *
 * Preview's chips describe the SUBMITTED answer, because that is what the student has been told
 * about. Behind the Scenes wants the other question -- what would fire if they pressed Submit
 * now -- and the two part company the moment someone edits after submitting, so one evaluation
 * cannot serve both. Keyed on the draft, so it runs once per distinct thing typed rather than
 * once per keystroke.
 */
const keywordDraftChipsCache: IssueCache<KeywordTestData> = new WeakMap();

// ─── Main component ───────────────────────────────────────────────────────────

export default function KeywordBlock({ block, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver, isMain = false, embedded, onClose, mobileIsEditing, initialIsEditingFeedback, onFeedbackModeChange, blocks, onRename }: KeywordBlockProps) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const isPhoneLandscape = useIsPhoneLandscape();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const {
    setTestVersion, shownVersionId,
    editorVersion, setEditorVersion, editorVersionId,
    ringEditorJump,
  } = useBlockVersions(block);
  /** Through the version: every `useState` seeds from this, and the block's own top-level copy is
   *  vestigial once a bank exists — see mcq-block.tsx's note. */
  const initial = useRef(applyOptionsVersion(initData(block.data), editorVersion));
  const shownVersions = (initData(block.data).versions ?? []) as { id: string }[];
  const questionHtmlRef = useRef(initial.current.question);
  /**
   * The question HTML the editor is HANDED, mirroring the ref beside it.
   *
   * The ref alone cannot do this job. It is the synchronous read — `onChange` writes it and calls
   * `push()` in the same breath, which is why it exists — but a ref does not re-render, so restored
   * HTML had no way onto the screen. Handing the editor a memo of the ref does not work either:
   * `RichTextEditor` syncs on its `content` PROP changing, and undoing back to the value the block
   * mounted with is a prop that never changed. So the state tracks every keystroke, exactly as the
   * other migrated blocks do, and costs nothing extra — `push()` already re-renders this component
   * on each one.
   */
  const [questionHtml, setQuestionHtml] = useState(initial.current.question);

  // Declared above the data state it decides the shape of: only the EDITING instance holds state,
  // and the collapsed card derives from props. See `use-restored-data`.
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !initial.current.question));
  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);

  const [editedOptions, setOptions] = useState<KeywordOption[]>(initial.current.options);
  const [editedCaseSensitive, setCaseSensitive] = useState(initial.current.caseSensitive);
  const [editedAccentSensitive, setAccentSensitive] = useState(initial.current.accentSensitive);
  const [editedGradingMode, setGradingMode] = useState(initial.current.gradingMode);
  const [editedConditionalEquation, setConditionalEquation] = useState<string | undefined>(initial.current.conditionalEquation);
  const [editedConditionalBranches, setConditionalBranches] = useState<ConditionalRoleBranch[] | undefined>(initial.current.conditionalBranches);
  // Unchecking "Correctness depends on variables" clears the equation/branches from state — but a
  // teacher who rechecks it before closing the editor shouldn't lose what they'd typed. Holds the
  // last-known equation/branches across an off→on cycle within the same editing session; cleared
  // for real in handleDone, so it never survives an exit while the toggle is left off.
  /**
   * The branch set switched off but not yet discarded, so an accidental untick (or deleting the last
   * variable chip) doesn't destroy the work. Seeded from `conditionalBranchesDraft` on the saved
   * block, which is what lets it survive a reload as well as a toggle. Dropped on close, and that is
   * what makes the removal permanent (recovery is undo).
   */
  const savedConditionalDraftRef = useRef<KeywordBlockData['conditionalBranchesDraft']>(initial.current.conditionalBranchesDraft);
  /**
   * Is the banked set OURS to offer back, or left over from a session long finished?
   *
   * `savedConditionalDraftRef` is seeded from the saved block so that closing clears it, which is
   * what garbage-collects one left behind. But offering it back is a different question. The
   * restore below already says "from the last time THIS SESSION turned the toggle off" — this is
   * what makes that true, where seeding alone had it resurrect a set banked weeks ago.
   *
   * Owner's reasoning, and it is the whole point: come back much later and a stored branching
   * correct you have long forgotten is confusing, not helpful. An untick is recoverable for as
   * long as you are still looking at the block; after that, recovery is undo.
   */
  const bankedThisSessionRef = useRef(false);
  const [editedAngleMode, setAngleMode] = useState<AngleMode>(initial.current.angleMode ?? 'degrees');

  /**
   * One stateful copy, not two — the card derives from props and cannot go stale. The echo
   * bookkeeping, and why its markers are state rather than refs, lives in `use-restored-data`.
   */
  const resyncFeedbackRef = useRef<((raw: unknown) => void) | null>(null);
  const edited = {
    options: editedOptions, caseSensitive: editedCaseSensitive, accentSensitive: editedAccentSensitive,
    gradingMode: editedGradingMode, conditionalEquation: editedConditionalEquation,
    conditionalBranches: editedConditionalBranches, angleMode: editedAngleMode,
  };
  const restored = useRestoredData<KeywordBlockData>({
    raw: block.data,
    isEditing,
    derive: (raw) => applyOptionsVersion(initData(raw as Parameters<typeof initData>[0]), editorVersion),
    // Every field the editor holds, question included. Leaving one out does not make the compare
    // safer, it makes it blind: with the question blanked, typing into an otherwise untouched block
    // compared equal and the re-seed never ran, so an undo moved the data and not the editor.
    current: { ...applyOptionsVersion(initData(block.data), editorVersion), ...edited, question: questionHtml } as KeywordBlockData,
    apply: seedEditorFrom,
  });

  /** Push one version's content into the editor's local state — an undo, another device's save
   *  and switching version are the same act from here. */
  function seedEditorFrom(next: KeywordBlockData) {
      setOptions(next.options);
      setCaseSensitive(next.caseSensitive);
      setAccentSensitive(next.accentSensitive);
      setGradingMode(next.gradingMode);
      setConditionalEquation(next.conditionalEquation);
      setConditionalBranches(next.conditionalBranches);
      setAngleMode(next.angleMode ?? 'degrees');
      savedConditionalDraftRef.current = next.conditionalBranchesDraft;
      // The ref is the synchronous read `push()` depends on, so it has to move with the state. The
      // write is idempotent, which is what makes it safe here: a discarded render leaves the ref
      // holding the same restored HTML the restarted one writes again.
      questionHtmlRef.current = next.question;
      setQuestionHtml(next.question);
      resyncFeedbackRef.current?.(next.feedback);
  }

  const seededVersionRef = useVersionSeeding(editorVersion, (id) => {
    seedEditorFrom(applyOptionsVersion(initData(block.data), id));
  });

  /**
   * Switch which version the EDITOR is pointed at, re-seeding every field from it.
   *
   * `from` is the data to read that version OUT of. It defaults to the block's own, which is right
   * everywhere except the two places it matters most: during an add or a remove, `block.data` is
   * still the PRE-change data, the new version's id is not in it, and `pickVersion` answers an id
   * it cannot find with `versions[0]`. So pressing "+" seeded the editor from VERSION 1 while the
   * pager read "Version N of N", and the copy only became wrong once the teacher typed into it.
   * A caller holding the post-change data passes it.
   */
  const selectVersion = (id: string | undefined, from = initData(block.data)) => {
    seededVersionRef.current = id;
    setEditorVersion(id);
    seedEditorFrom(applyOptionsVersion(from, id));
  };

  /** Open the editor on what the test panel is showing. The one bridge between the pointers. */
  const editShownVersion = (branchId?: string) => {
    if (shownVersionId) selectVersion(shownVersionId);
    void ringEditorJump(branchId);
  };
  const options = isEditing ? editedOptions : restored.data.options;
  const caseSensitive = isEditing ? editedCaseSensitive : restored.data.caseSensitive;
  const accentSensitive = isEditing ? editedAccentSensitive : restored.data.accentSensitive;
  const gradingMode = isEditing ? editedGradingMode : restored.data.gradingMode;
  const conditionalEquation = isEditing ? editedConditionalEquation : restored.data.conditionalEquation;
  const conditionalBranches = isEditing ? editedConditionalBranches : restored.data.conditionalBranches;
  const angleMode = isEditing ? editedAngleMode : (restored.data.angleMode ?? 'degrees');

  // Transient equation defects wait for the equation to stop changing. Two filters, not one: the
  // card and test panel read an issue list built from `block.data`, the editor panel reads one
  // built from live state, and the two are often rendered by different instances of this
  // component — so each must be gated by the settling of the value it was computed from.
  // No `answers` here: this block's answers are not equations, only its branching selector is.
  const { settleLive, settleStored } = useIssueSettling(
    { equation: conditionalEquation },
    { equation: initData(block.data).conditionalEquation },
  );

  /** Every write out of this block, so its own echo is never mistaken for an undo. */
  const emit = (data: Record<string, unknown>) => { restored.markEmitted(data); onUpdate(data); };

  /* One version per row of the driver's answer space. Nothing about this block is
     named here: `addVersion` already knows its answer key, which is what keeps this
     to one line in twelve places. See `useDrivenVersionProjection`. */
  useDrivenVersionProjection({ block, data: initData(block.data), emit, addVersion: addVersion });

  /** Every formula field in this block, wherever it sits — see `AngleModeProvider`. The merge
   *  lives in the hook so no block can delete its siblings' modes by writing only its own, and
   *  the spread is `block.data` rather than the editor's view, which is `initData`'s output and
   *  omits whatever a block threads separately — numerical's feedback, famously. */
  const angleModeProps = useBlockAngleModes(
    block.data,
    patch => emit({ ...block.data, ...patch }),
  );

  /**
   * EVERY field write goes through here, because `onUpdate` REPLACES `block.data` wholesale: a
   * write that builds its object field by field deletes everything it forgot to name. Taking a
   * PARTIAL is the point — a caller can only state what it is changing. See mcq-block.tsx, where
   * two such writes forgot `versions` and destroyed the bank on the way into editing it.
   */
  const emitFields = (next: Partial<KeywordBlockData>) => {
    const base = initData(block.data);
    emit(writeOptionsVersion(
      base, editorVersion, { ...base, ...next } as KeywordBlockData,
    ) as unknown as Record<string, unknown>);
  };

  const [activeBranchIndex, setActiveBranchIndex] = useBranchJumpTarget(block.id);

  const currentDataRef = useRef({ options, caseSensitive, accentSensitive, gradingMode, conditionalEquation, conditionalBranches });
  currentDataRef.current = { options, caseSensitive, accentSensitive, gradingMode, conditionalEquation, conditionalBranches };

  // Feedback is stale (suppressed, not deleted) once its target keyword is marked correct —
  // hidden here rather than reassigned to a different keyword in the trigger picker.

  // The positive-slot's three known default bodies — see retriggerPositiveSlot's doc comment.
  const positiveSlotContent = {
    correct: `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`,
    anyAnswer: `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`,
    anyResponse: `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
  };
  const positiveSlotTitle =
    gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle') :
    gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle') :
    t('blocks.feedbackDefaults.anyResponseTitle');

  const { feedback, setFeedback, resyncFeedback, getTitle } = useInputBlockFeedback({
    initialRaw: initial.current.feedback,
    defaults: {
      unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
      unansweredContent: `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>`,
      incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
      incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
      correctTitle: positiveSlotTitle,
      correctContent: gradingMode === 'graded' ? positiveSlotContent.correct : gradingMode === 'survey' ? positiveSlotContent.anyAnswer : positiveSlotContent.anyResponse,
    },
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct: positiveSlotTitle,
      incorrect: t('blocks.feedbackDefaults.incorrectTitle'),
    },
    positiveTrigger: positiveTriggerForMode(gradingMode),
    onUpdate: (nextFeedback) => {
      const { options, caseSensitive, accentSensitive, gradingMode } = currentDataRef.current;
      emitFields({ question: questionHtmlRef.current, options, caseSensitive, accentSensitive, gradingMode, feedback: nextFeedback });
    },
  });

  resyncFeedbackRef.current = resyncFeedback;

  // ── Branch-scoped feedback indirection ──────────────────────────────────────
  // Every read/write below that deals with feedback CONTENT goes through these instead of
  // `feedback`/`updateFeedbackState`/`deleteFeedbackState`/`isDupTitle` directly, so the same
  // JSX-construction code works unchanged whether branching is on or off — see DropdownBlock's
  // identical indirection layer for the full reasoning. Gated on gradingMode itself, not just
  // conditionalBranches existing — branches are authored exclusively in graded mode.
  const scenario = useScenarioFollower<ScenarioRoleBranch<KeywordFeedback>>({
    block, blocks, gradingMode, feedback, isEditing, isPreview,
    storedData: initData(block.data) as never,
    restoredRows: restored.data.scenarioBranches,
    initialRows: initial.current.scenarioBranches,
    emit: data => emit(versionStructure(block.data, data)),
    applyVersion: applyOptionsVersion,
    emitFields: next => emitFields(next as Partial<KeywordBlockData>),
    rowTemplate: () => ({
      roles: Object.fromEntries(options.map(o => [o.id, o.role])),
      feedback,
    }),
    rowFromVersion: v => ({
      roles: (v as unknown as RoleVersion<KeywordFeedback>).roles,
      feedback: (v as unknown as RoleVersion<KeywordFeedback>).feedback,
    }),
    numeric: {
      read: () => ({ equation: conditionalEquation, branches: conditionalBranches }),
      write: (eq, br) => {
        setConditionalEquation(eq as string | undefined);
        setConditionalBranches(br as ConditionalRoleBranch[] | undefined);
      },
    },
  });

  const activeBranch = gradingMode === 'graded' && conditionalBranches && conditionalBranches.length > 0
    ? conditionalBranches[Math.min(activeBranchIndex, conditionalBranches.length - 1)]
    : undefined;
  /* WHICHEVER setup the panel is editing. A numeric branch and a scenario row differ only in
     what SELECTS them, and every read below this line is one path. */
  const activeSetup: { roles: Record<string, KeywordOption['role']>; feedback: KeywordFeedback } | undefined
    = scenario.activeScenario ?? activeBranch;
  const activeFeedbackStates = activeSetup ? activeSetup.feedback.states : feedback.states;

  function setActiveFeedbackStates(nextStates: FeedbackState[]) {
    if (scenario.activeScenario) {
      scenario.writeActiveRow({ feedback: { states: nextStates } });
    } else if (activeBranch) {
      const nextBranches = (conditionalBranches ?? []).map(b => b.id === activeBranch.id ? { ...b, feedback: { states: nextStates } } : b);
      setConditionalBranches(nextBranches);
      push(options, caseSensitive, accentSensitive, gradingMode, feedback, nextBranches);
    } else {
      const next = { states: nextStates };
      setFeedback(next);
      push(options, caseSensitive, accentSensitive, gradingMode, next, conditionalBranches);
    }
  }

  function updateActiveFeedbackState(id: string, patch: Partial<FeedbackState>) {
    setActiveFeedbackStates(activeFeedbackStates.map(s => (s.id === id ? { ...s, ...patch } : s)));
  }

  function deleteActiveFeedbackState(id: string) {
    const state = activeFeedbackStates.find(s => s.id === id);
    if (!state || state.type === 'default') return;
    setActiveFeedbackStates(activeFeedbackStates.filter(s => s.id !== id));
  }

  // Role-per-keyword for whichever branch is active — same construction the option-list editor
  // already builds inline for its own display; centralized here so title-collision/staleness
  // checks against the active branch's own feedback can share it too.
  const effectiveOptionsForActive: KeywordOption[] = rolesForMode(gradingMode, activeBranch
    ? options.map(o => ({ ...o, role: activeBranch.roles[o.id] ?? ('neutral' as const) }))
    : options);

  function isTargetedStaleForActive(s: FeedbackState): boolean {
    /**
     * A blank only fires in OPTIONAL. Graded and survey catch it as `unanswered` BEFORE anything
     * targeted runs, so the card could never fire there — it sat in the panel looking authored
     * and resolving to nothing.
     *
     * Hidden rather than deleted, like every other stale targeted state: switching back to
     * optional brings it and its text back, which is the same promise targeted feedback makes
     * across every other mode change.
     */
    if (s.type !== 'targeted' || !s.trigger.startsWith('option_')) return false;
    const id = s.trigger.slice('option_'.length);
    return effectiveOptionsForActive.find(o => o.id === id)?.role === 'correct';
  }

  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  const shouldFocusEditor = useRef(false);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);

  const [armedOptionId, setArmedOptionId] = useState<string | null>(null);
  const sharedToolbar = useSharedEditorToolbar();
  
  const { setActiveEditor, activeEditorRef } = sharedToolbar;
  const questionEditorRef = useRef<Editor | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  // Keyboard-only focus lands on <body> once a row's own remove button unmounts — a stable,
  // always-present fallback to refocus, so a keyboard/screen-reader user's place in the editor
  // isn't lost on every delete. branchPrevButtonRef is the equivalent for removing a branch.
  const addOptionButtonRef = useRef<HTMLButtonElement | null>(null);
  const branchPrevButtonRef = useRef<HTMLButtonElement | null>(null);

  // Live test state for the collapsed canvas card — only meaningful while isSelected (this
  // block's own editor is open elsewhere) — mirrors DropdownBlock's own identical state exactly
  // (see its doc comment for the full reasoning). testAnswer is the widget's own live value;
  // submittedAnswer is what evaluation actually runs against — the two only diverge in
  // submit-gated mode (continuous === false).
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);
  const [ownRolledValues, setRolledValues] = useState<Record<string, string>>({});
  // Preview must show ONE task-level roll shared by every block (see TaskPreviewRollContext): a task
  // whose blocks all read {n} has to agree about n, and seeding Preview from this block's own roll made
  // them disagree. Block-level testing keeps its private roll plus reroll, which is the whole point of
  // it — a teacher steps through combinations for the one block they're testing.
  const taskPreviewRoll = useTaskPreviewRoll();
  const rolledValues = isPreview ? (taskPreviewRoll ?? {}) : ownRolledValues;
  /**
   * Seeded from the last attempt on a revisit, so walking back into a task does not wipe it.
   *
   * Read ONCE, in the initializer: Preview remounts blocks on navigation and on Reset, so a block
   * that re-seeded on every render would be fighting the student's own typing for no gain.
   */
  const prior = usePriorAnswer<string>();
  const [testAnswer, setTestAnswer] = useState(prior ?? '');
  const [submittedAnswer, setSubmittedAnswer] = useState(prior ?? '');
  const [hasSubmitted, setHasSubmitted] = useState(prior !== undefined);
  // The SUBMITTED answer, never the draft -- recording the draft would make the attempt a
  // snapshot of the student's typing rather than of what they chose to hand in.
  useReportAnswer(hasSubmitted ? submittedAnswer : undefined);
  const [continuous, setContinuous] = useState(true);
  // Phone-only Edit/Test toggle inside the full-screen editor — see InputBlockFrame's own doc
  // comment and DropdownBlock's identical `phoneTestMode` state.
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');
  useEffect(() => {
    // Also seeded for `isPreview` — the student-facing Preview render needs a real rolled value
    // exactly like the isSelected test widget does, rather than showing raw unresolved variable
    // chips.
    if (isSelected) {
      setRolledValues(rollVariableValues(variables));
    // NOT in Preview. This branch exists for the EDITOR, where deselecting a block clears the
      // test widget it was driving. Preview has no selection, so `isSelected` is false forever and
      // this ran on mount, wiping the answer a revisit had just restored. Preview's own "start
      // again" is the remount `previewResetKey` forces, not this.
      } else if (!isPreview) {
      setTestAnswer('');
      setSubmittedAnswer('');
      setHasSubmitted(false);
      setContinuous(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, isPreview]);
  // Phone's own entry/exit into the Test tab — mirrors the isSelected effect above exactly (see
  // DropdownBlock's identical effect for why isSelected alone never fires on phone).
  useEffect(() => {
    if (phoneTestMode === 'test') {
      setRolledValues(rollVariableValues(variables));
    // NOT in Preview, for the same reason as the `isSelected` effect above: this clears the
    // test widget when the PHONE editor leaves its Test tab, and on a desktop Preview
    // `phoneTestMode` is 'edit' forever, so it ran on mount and wiped the restored answer.
    } else if (!isPreview) {
      setTestAnswer('');
      setSubmittedAnswer('');
      setHasSubmitted(false);
      setContinuous(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneTestMode]);
  // Preview's single bottom Submit — reacts to the signal CHANGING, not a per-block Submit click,
  // reusing the exact same submittedAnswer/hasSubmitted state the live test widget already uses.
  useEffect(() => {
    if (isPreview && previewSubmitSignal) {
      setSubmittedAnswer(testAnswer);
      setHasSubmitted(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);

  function handleTestChange(text: string) {
    setTestAnswer(text);
    if (continuous) { setSubmittedAnswer(text); setHasSubmitted(true); }
  }
  function handleTestSubmit() {
    setSubmittedAnswer(testAnswer);
    setHasSubmitted(true);
  }
  function handleContinuousChange(next: boolean) {
    setContinuous(next);
    if (next) { setSubmittedAnswer(testAnswer); setHasSubmitted(true); }
  }
  function handleTestReset() {
    setTestAnswer('');
    setSubmittedAnswer('');
    setHasSubmitted(false);
  }
  // A result belongs to the version it was taken on; stepping the pager retires it.
  useTestResetOnVersionChange(shownVersionId, handleTestReset);
  function handleTestReroll() {
    setRolledValues(rollVariableValues(variables));
  }

  function push(opts = options, cs = caseSensitive, as = accentSensitive, mode = gradingMode, fdbk = feedback, cb = conditionalBranches, eq = conditionalEquation, angMode = angleMode) {
    // The retained draft rides along on every ordinary save, so editing anything else cannot
    // drop it. Deliberately not a parameter: a defaulted parameter cannot express "clear this".
    emitFields({ question: questionHtmlRef.current, options: opts, caseSensitive: cs, accentSensitive: as, gradingMode: mode, feedback: fdbk, conditionalEquation: eq, conditionalBranches: cb, conditionalBranchesDraft: savedConditionalDraftRef.current, angleMode: angMode });
  }

  /**
   * Persists branching as CLEARED. Needed because `push` cannot express it: a JS default parameter
   * fires on `undefined`, so `push(..., undefined, undefined)` does not clear the branches, it
   * restores `cb = conditionalBranches` — the very values being removed. Toggling branching off
   * therefore looked like it worked (the UI reads local state, already set to undefined) while the
   * saved block kept its branches: the canvas card, which grades off the saved block, went on
   * resolving a branch the editor said was off, and reopening the block showed the toggle back on.
   *
   * The authored set is still recoverable for as long as this editor stays open — the draft is held
   * in `savedConditionalDraftRef` and re-ticking the box restores it. Closing drops the draft, and
   * from there recovery is undo.
   */
  /**
   * Every write that changes the branch state itself, stated explicitly. It cannot go through `push`:
   * a JS default parameter fires on `undefined`, so `push(..., undefined, undefined)` restores
   * `cb = conditionalBranches` rather than clearing it — the trap that made "turn branching off"
   * silently never stick, leaving the canvas card grading against branches the editor said were off.
   */
  function pushBranchState(
    branches: ConditionalRoleBranch[] | undefined,
    equation: string | undefined,
    draft: KeywordBlockData['conditionalBranchesDraft'],
  ) {
    emitFields({ question: questionHtmlRef.current, options, caseSensitive, accentSensitive, gradingMode, feedback, conditionalEquation: equation, conditionalBranches: branches, conditionalBranchesDraft: draft, angleMode });
  }

  function handleGradingMode(mode: KeywordBlockData['gradingMode']) {
    const nextFeedbackStates = retriggerPositiveSlot(feedback.states, mode, positiveSlotContent);
    if (nextFeedbackStates !== feedback.states) setFeedback({ states: nextFeedbackStates });

    setGradingMode(mode);
    push(options, caseSensitive, accentSensitive, mode, { states: nextFeedbackStates });
  }

  function handleCaseSensitive(val: boolean) {
    setCaseSensitive(val);
    push(options, val, accentSensitive, gradingMode);
  }

  function handleAccentSensitive(val: boolean) {
    setAccentSensitive(val);
    push(options, caseSensitive, val, gradingMode);
  }

  /** Applies a resync/filter transform to EVERY independent feedback set a keyword block has —
   * the top-level one plus each branch's own — since a keyword's text, position, or removal
   * affects every targeted feedback that references it, not just whichever branch happens to be
   * active in the scroller right now. Mirrors DropdownBlock's identical helper. */
  function updateAllFeedbackSets(
    transform: (states: FeedbackState[]) => FeedbackState[],
  ): { feedback: KeywordBlockData['feedback']; branches: ConditionalRoleBranch[] | undefined } {
    return {
      feedback: { states: transform(feedback.states) },
      branches: conditionalBranches?.map(b => ({ ...b, feedback: { states: transform(b.feedback.states) } })),
    };
  }

  function updateOptionText(id: string, text: string) {
    const next = options.map(o => o.id === id ? { ...o, text } : o);
    // Keep any feedback targeting this keyword's title in sync with its new text — otherwise
    // renaming "cat" to "feline" would leave a stale "Selected cat" title behind. Applied across
    // every branch's own feedback too, not just whichever is active.
    const { feedback: nextFeedback, branches: nextBranches } = updateAllFeedbackSets(
      states => resyncKeywordTargetedTitles(next, states, t)
    );
    setOptions(next);
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, caseSensitive, accentSensitive, gradingMode, nextFeedback, nextBranches ?? conditionalBranches);
  }

  /** Static (no-branch) role cycle — used only when `!activeBranch`; a branch's own role click
   * routes to handleBranchRoleClick instead. */
  function handleRoleClick(id: string) {
    const CYCLE: KeywordOption['role'][] = ['neutral', 'correct', 'incorrect'];
    const next = options.map(o => {
      if (o.id !== id) return o;
      const idx = CYCLE.indexOf(o.role);
      return { ...o, role: CYCLE[(idx + 1) % CYCLE.length]! };
    });
    setOptions(next);
    // Every targeted feedback's evaluation mirrors its target keyword's role — keep them in
    // sync whenever a role changes, rather than letting the two silently drift apart.
    const nextFeedback = { states: resyncKeywordTargetedEvaluations(next, feedback.states) };
    setFeedback(nextFeedback);
    push(next, caseSensitive, accentSensitive, gradingMode, nextFeedback);
  }

  function addOption() {
    const next = [...options, makeOption()];
    // A new keyword needs a role entry in every existing branch too, or its role would silently
    // read as `undefined` (falling back to 'neutral' in effectiveOptionsForActive, but never
    // actually present in the data) until a teacher happens to click it.
    const nextBranches = conditionalBranches?.map(b => ({ ...b, roles: { ...b.roles, [next[next.length - 1]!.id]: 'neutral' as const } }));
    setOptions(next);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, caseSensitive, accentSensitive, gradingMode, feedback, nextBranches ?? conditionalBranches);
  }

  function removeOption(id: string) {
    if (options.length <= 1) return;
    const next = options.filter(o => o.id !== id);
    // Deleting a keyword also deletes any feedback tied to it — recoverable only through undo,
    // not silently left behind as an orphaned, mislabeled card. Remaining feedback titles are
    // resynced too, since a blank keyword's fallback label is position-based and every keyword
    // after the removed one just shifted down by one. Applied across every branch's own feedback,
    // not just whichever is active — a keyword disappearing affects all of them.
    const { feedback: nextFeedbackFromSets, branches: nextBranchesFromFeedback } = updateAllFeedbackSets(
      states => resyncKeywordTargetedTitles(next, states.filter(s => !(s.type === 'targeted' && s.trigger === `option_${id}`)), t)
    );
    // A branch's OWN `roles` map isn't touched by the reassignment above (that's only the static
    // `options` array) — the now-meaningless stale entry for the removed keyword id is dropped.
    // Unlike Dropdown/MCQ single-select, no auto-promotion of a replacement correct keyword here:
    // multiple (or zero) correct keywords are a normal, valid state for Keyword, so there's no
    // single "right" substitute to pick — a branch left with zero correct keywords simply
    // surfaces as keywordBranchRoleIssue's 'none-correct', same as the static case below.
    const nextBranches = nextBranchesFromFeedback?.map(b => ({
      ...b,
      roles: Object.fromEntries(Object.entries(b.roles).filter(([optId]) => optId !== id)),
    }));
    setOptions(next);
    setFeedback(nextFeedbackFromSets);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, caseSensitive, accentSensitive, gradingMode, nextFeedbackFromSets, nextBranches ?? conditionalBranches);
    // The just-removed row's own remove button (whatever had focus) unmounts with it, dropping
    // keyboard focus to <body> — refocus the one control guaranteed to still exist afterward.
    addOptionButtonRef.current?.focus();
  }

  function handleOptionDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const from = options.findIndex(o => o.id === active.id);
    const to = options.findIndex(o => o.id === over.id);
    if (from === -1 || to === -1) return;
    const next = arrayMove(options, from, to);
    setOptions(next);
    const { feedback: nextFeedback, branches: nextBranches } = updateAllFeedbackSets(
      states => resyncKeywordTargetedTitles(next, states, t)
    );
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(next, caseSensitive, accentSensitive, gradingMode, nextFeedback, nextBranches ?? conditionalBranches);
  }

  /** Backfills default feedback text/titles when a fresh branch's own body is cloned from the
   * top-level feedback that was active the moment branching was first turned on — fresh ids so
   * updating branch 1's Correct card can never ambiguously also match the top-level Correct card
   * sharing the same id. Mirrors DropdownBlock's identical `cloneFeedbackWithFreshIds`. */
  function cloneFeedbackWithFreshIds(source: KeywordBlockData['feedback']): KeywordBlockData['feedback'] {
    return { states: source.states.map(s => ({ ...s, id: crypto.randomUUID() })) };
  }

  /** Seeds every branch after the first from whichever branch was active when "add" was clicked
   * (always the last one) — structure carries over, content resets to blank if customized, or
   * carries forward unchanged if it still matches that slot's default. Was NOT actually mirroring
   * DropdownBlock/MCQBlock's identical `seedBranchFeedback` despite the doc comment's claim — this
   * used to carry forward ANY non-empty content verbatim (including a teacher's own customized
   * targeted-feedback text), silently duplicating a message written for one branch into every
   * branch forked afterward. Fixed to actually call resetContentIfCustomized, matching Dropdown/MCQ. */
  function seedBranchFeedback(source: KeywordBlockData['feedback']): KeywordBlockData['feedback'] {
    return {
      states: source.states.map(s => {
        const defaultContent = s.type !== 'default' ? '' :
          s.trigger === 'unanswered' ? `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>` :
          s.trigger === 'incorrect' ? `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>` :
          // Branching only exists in graded mode, so the positive slot inside a branch is always
          // the 'correct' trigger, never any_answer/any_response.
          positiveSlotContent.correct;
        return { ...s, id: crypto.randomUUID(), content: resetContentIfCustomized(s.content, defaultContent) };
      }),
    };
  }

  function makeConditionalBranch(seedFrom: KeywordBlockData['feedback'], mode: 'migrate' | 'fork'): ConditionalRoleBranch {
    return {
      id: crypto.randomUUID(),
      operator: '==',
      threshold: '0',
      roles: Object.fromEntries(options.map(o => [o.id, o.role])),
      feedback: mode === 'migrate' ? cloneFeedbackWithFreshIds(seedFrom) : seedBranchFeedback(seedFrom),
    };
  }

  function toggleConditionalCorrectness(enabled: boolean) {
    if (enabled) {
      // Restore the draft from the last time this session turned the toggle off, if any —
      // otherwise start fresh, same as before.
      const draft = bankedThisSessionRef.current ? savedConditionalDraftRef.current : undefined;
      const equation = draft?.equation ?? '';
      const branches = draft?.branches ?? [makeConditionalBranch(feedback, 'migrate')];
      setConditionalEquation(equation);
      setConditionalBranches(branches);
      setActiveBranchIndex(0);
      savedConditionalDraftRef.current = undefined;
      push(options, caseSensitive, accentSensitive, gradingMode, feedback, branches, equation);
    } else {
      // Banked under its own key and out of `conditionalBranches` at once. Recovery from an
      // accidental untick needs the set to survive, but the canvas card grades off the saved block,
      // so leaving it in place would have the card resolving a branch the editor says is off. A
      // separate key gives both, and unlike a memory-only draft it survives a reload.
      const draft = conditionalBranches !== undefined
        ? { equation: conditionalEquation ?? '', branches: conditionalBranches }
        : savedConditionalDraftRef.current;
      savedConditionalDraftRef.current = draft;
      bankedThisSessionRef.current = draft !== undefined;
      setConditionalEquation(undefined);
      setConditionalBranches(undefined);
      pushBranchState(undefined, undefined, draft);
    }
  }

  // A variable's presence in the question is what makes branch-based correctness meaningful at
  // all (keyword text is a plain string, so it can never itself hold a variable chip — see the
  // port plan) — showConditionalUI (in the editingSlot JSX) hides the whole toggle+equation+branch
  // UI once the last variable chip is removed, but hiding it isn't enough on its own: the branches
  // would still sit there, still consulted by evaluateKeyword. Runs every render (no dependency
  // array) rather than trying to track `questionHtmlRef.current` — mirrors DropdownBlock's
  // identical effect for the full reasoning.
  useEffect(() => {
    if (!isEditing) return;
    const hasVariableNow = htmlHasVariableChips(questionHtmlRef.current);
    if (!hasVariableNow && conditionalBranches !== undefined) toggleConditionalCorrectness(false);
  });

  function updateConditionalEquation(val: string) {
    setConditionalEquation(val);
    push(options, caseSensitive, accentSensitive, gradingMode, feedback, conditionalBranches, val);
  }

  function addConditionalBranch() {
    const branches = conditionalBranches ?? [];
    const seedFrom = branches.length > 0 ? branches[branches.length - 1]!.feedback : feedback;
    const next = [...branches, makeConditionalBranch(seedFrom, 'fork')];
    setConditionalBranches(next);
    setActiveBranchIndex(next.length - 1);
    push(options, caseSensitive, accentSensitive, gradingMode, feedback, next);
  }

  function removeConditionalBranch(id: string) {
    const next = (conditionalBranches ?? []).filter(b => b.id !== id);
    setConditionalBranches(next);
    setActiveBranchIndex(i => Math.max(0, Math.min(i, next.length - 1)));
    push(options, caseSensitive, accentSensitive, gradingMode, feedback, next);
    branchPrevButtonRef.current?.focus();
  }

  function updateConditionalBranch(id: string, patch: Partial<Pick<ConditionalRoleBranch, 'operator' | 'threshold' | 'thresholdHigh'>>) {
    const next = (conditionalBranches ?? []).map(b => b.id === id ? { ...b, ...patch } : b);
    setConditionalBranches(next);
    push(options, caseSensitive, accentSensitive, gradingMode, feedback, next);
  }

  /** Simpler than MCQ/Dropdown's version — no `allowMultiple` fork needed at all: Keyword has no
   * select-mode toggle, and multiple keywords marked correct within one branch is a normal,
   * intended pattern (synonyms), so a branch role click just cycles neutral→correct→incorrect for
   * that one keyword, unconditionally, same as the static (no-branch) role cycle. */
  /** What a role click means here — the three-way cycle, shared by the numeric branch and the
   *  scenario row so the two cannot disagree about what pressing the same button does. */
  function rolesAfterRoleClick(roles: Record<string, KeywordOption['role']>, optionId: string) {
    const CYCLE: KeywordOption['role'][] = ['neutral', 'correct', 'incorrect'];
    const cur = roles[optionId] ?? 'neutral';
    return { ...roles, [optionId]: CYCLE[(CYCLE.indexOf(cur) + 1) % CYCLE.length]! };
  }

  /** Every targeted feedback's evaluation mirrors its target keyword's role WITHIN this setup —
   *  the same resync `handleRoleClick` runs for the static case. */
  function resyncedForRoles(roles: Record<string, KeywordOption['role']>, states: FeedbackState[]) {
    return resyncKeywordTargetedEvaluations(
      options.map(o => ({ ...o, role: roles[o.id] ?? ('neutral' as const) })), states);
  }

  function handleBranchRoleClick(branchId: string, optionId: string) {
    const next = (conditionalBranches ?? []).map(b => {
      if (b.id !== branchId) return b;
      const nextRoles = rolesAfterRoleClick(b.roles, optionId);
      return { ...b, roles: nextRoles, feedback: { states: resyncedForRoles(nextRoles, b.feedback.states) } };
    });
    setConditionalBranches(next);
    push(options, caseSensitive, accentSensitive, gradingMode, feedback, next);
  }

  /** The same act keyed by variant instead of by branch id. Deciding a row's roles IS reviewing
   *  it, which `writeActiveRow` handles. */
  function handleScenarioRoleClick(optionId: string) {
    const row = scenario.activeScenario;
    if (!row) return;
    const nextRoles = rolesAfterRoleClick(row.roles, optionId);
    scenario.writeActiveRow({
      roles: nextRoles,
      feedback: { states: resyncedForRoles(nextRoles, row.feedback.states) },
    } as never);
  }

  function rebuildStates(targeteds: FeedbackState[]): FeedbackState[] {
    const defaults = activeFeedbackStates.filter(s => s.type === 'default');
    return [
      ...defaults.filter(s => s.trigger === 'unanswered'),
      ...targeteds,
      ...defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'),
      ...defaults.filter(s => s.trigger === 'incorrect'),
    ];
  }

  /** The card the Add button last produced — the one thing that opens a feedback card on mount.
   *  State, not a ref: it is read during render, and a ref mutated in a discarded render keeps a
   *  value that render threw away. */
  /**
   * The card the Add button last produced — the one thing that opens a feedback card on mount.
   *
   * Keyed BY MODE. Reordering the panel's sections on a mode switch remounts these cards, and a
   * bare id would still name one of them, so the last-added card sprang open on every switch —
   * the behaviour this was meant to remove. State, not a ref: it is read during render, and a ref
   * mutated in a discarded render keeps a value that render threw away.
   */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  const justAddedKey = (id: string) => `${gradingMode}:${id}`;

  function addTargetedState() {
    const usedTriggers = new Set(activeFeedbackStates.filter(s => s.type === 'targeted').map(s => s.trigger));

    /**
     * A blank is one of the answers an OPTIONAL block can receive, so it belongs in the same list
     * as the answers — which is also what lets the catch-all close once the list is complete.
     * Appended like any other card; the list order is the teacher's.
     */

    // Outside graded no word is correct, so every one of them can carry a targeted state.
    const firstAvailableIdx = effectiveOptionsForActive.findIndex(o =>
      (gradingMode !== 'graded' || o.role !== 'correct') && !usedTriggers.has(`option_${o.id}`));
    const firstAvailable = firstAvailableIdx === -1 ? undefined : effectiveOptionsForActive[firstAvailableIdx];
    if (!firstAvailable) return;
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: keywordTargetedTitle(firstAvailable, t),
      trigger: `option_${firstAvailable.id}`,
      content: '', evaluation: firstAvailable.role === 'neutral' ? 'neutral' : 'incorrect',
    };
    setActiveFeedbackStates(rebuildStates([...targeteds, newState]));
  }

  function handleFeedbackTargetedDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const from = targeteds.findIndex(s => s.id === String(active.id));
    const to = targeteds.findIndex(s => s.id === String(over.id));
    if (from === -1 || to === -1) return;
    setActiveFeedbackStates(rebuildStates(arrayMove(targeteds, from, to)));
  }

  function handleDone() {
    push();
    // Closing is what makes a switched-off branch set unrecoverable: the retained draft is
    // dropped from the saved block and undo becomes the only way back.
    if (savedConditionalDraftRef.current !== undefined && conditionalBranches === undefined) pushBranchState(undefined, undefined, undefined);
    savedConditionalDraftRef.current = undefined;
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  // ── Grading mode metadata ─────────────────────────────────────────────────

  const MODES = gradingModes(t);
  const currentMode = MODES.find(m => m.id === gradingMode) ?? MODES[0]!;
  const nextMode = MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]!;

  // ── Preview ───────────────────────────────────────────────────────────────

  if (isPreview) {
    // Genuinely interactive now, not a disabled snapshot — Preview behaves exactly like the live
    // test widget (type an answer, see real feedback once submitted), just triggered by the
    // page-level bottom Submit (see the previewSubmitSignal effect above) instead of a per-block
    // one. rolledValues is seeded by the same effect the isSelected branch below uses, gated on
    // `isPreview` too. `onChange` is a plain setter, never the continuous-testing auto-submit
    // handler — mirrors DropdownBlock's own Preview wiring exactly (a real prior bug in MCQ).
    const rawD = initData(block.data);
    // The version THIS student drew, from the task roll — see `versionRollKey`.
    const d = buildNormalizedKeywordData(rawD, t, taskPreviewRoll?.[versionRollKey(block.id)]);
    const { resolvedD, testResult } = memoizedByKey(
      /* The DRAWN VERSION is part of the key, or this cache answers for whichever version it saw
         first and the content never moves — see mcq-block.tsx's note. */
      keywordTestDataCache, block.data,
      [variables, t, rolledValues, submittedAnswer, hasSubmitted, taskPreviewRoll?.[versionRollKey(block.id)],
       /* The LEADER's draw belongs in the key for the same reason this block's own does: the
          scenario decides the answer key. */
       drawnScenarioId(rawD.followsBlockId, taskPreviewRoll)],
      // The ONE task roll both blocks read, which is what makes the leader's prose and this
      // block's answer key describe the same scenario (§3.1).
      () => computeKeywordTestData(d, variables, t, rolledValues, submittedAnswer, hasSubmitted,
        drawnScenarioId(rawD.followsBlockId, taskPreviewRoll)),
    );
    /* The same derivation, asked about the draft instead: `hasSubmitted` is forced true so the
       chips light as though this answer had been handed in. Reported, never rendered -- Preview
       must still show a student nothing until they submit. */
    const { testChips: draftChips, testResult: draftStates } = memoizedByKey(
      keywordDraftChipsCache, block.data,
      [variables, t, rolledValues, testAnswer, taskPreviewRoll?.[versionRollKey(block.id)],
       drawnScenarioId(rawD.followsBlockId, taskPreviewRoll)],
      () => computeKeywordTestData(d, variables, t, rolledValues, testAnswer, true,
        drawnScenarioId(rawD.followsBlockId, taskPreviewRoll)),
    );
    return (
      <KeywordAnswerDisplay
          scenario={scenario.ghostedScenario}
        question={resolvedD.question}
        rolledValues={rolledValues}
        value={testAnswer}
        onChange={setTestAnswer}
      >
        {/* The block is the only thing that knows WHICH of its conditions are in force: the
            version drawn, the roll, and the scenario its leader drew all feed the derivation
            just above. Behind the Scenes reads this rather than re-deriving it from the base
            data, which is what once told a teacher the answer was Positive on a block whose
            scenario had swapped it. */}
        <ReportTestChips chips={draftChips} states={draftStates} />
        {renderKeywordFiredFeedback(testResult, rolledValues)}
      </KeywordAnswerDisplay>
    );
  }

  // ── Collapsed ─────────────────────────────────────────────────────────────

  if (!isEditing) {
    const rawD = initData(block.data);
    // Same backfill Preview/the phone Test tab rely on — see buildNormalizedKeywordData's own doc
    // comment for why a brand-new block otherwise shows empty legendStates/testChips.
    // Through the version in force, so the card tests what the pager is pointed at rather than
    // always the first — and so Reroll can move it.
    const d = buildNormalizedKeywordData(rawD, t, shownVersionId);
    const { matchedBranch, matchedBranchIndex, matchedEquationValue, testResult, testChips, hasChips, hasRollable } =
      memoizedByKey(
        // Keyed on the shown version too — see the preview branch's note.
        keywordTestDataCache, block.data,
        [variables, t, rolledValues, submittedAnswer, hasSubmitted, shownVersionId],
        /* On the CARD the scenario is whichever one the pager is on, never a draw — a draw
           here would be a different scenario from the one the editor is set up for. */
        () => computeKeywordTestData(d, variables, t, rolledValues, submittedAnswer, hasSubmitted,
          scenario.activeScenario?.scenarioId),
      );
    const displayMode = MODES.find(m => m.id === d.gradingMode) ?? MODES[0]!;
    const isGraded = d.gradingMode === 'graded';
    const correctKeywordsText = isGraded
      ? d.options.filter(o => o.role === 'correct' && o.text.trim()).map(o => o.text).join(', ')
      : '';
    const neutralCount = isGraded ? d.options.filter(o => o.role === 'neutral' && o.text.trim()).length : 0;
    const incorrectCount = isGraded ? d.options.filter(o => o.role === 'incorrect' && o.text.trim()).length : 0;
    const keywordNoteParts = [
      neutralCount > 0 ? t('blocks.keyword.flaggedKeywordCount', { count: neutralCount }) : null,
      incorrectCount > 0 ? t('blocks.keyword.forbiddenKeywordCount', { count: incorrectCount }) : null,
    ].filter((s): s is string => Boolean(s));
    const dispChipClass = d.gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted';
    const keywordAriaLabel = d.question
      ? t('blocks.keyword.editAriaBaseWithQuestion', { question: d.question.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) })
      : t('blocks.keyword.editAriaBase');

    // Every currently-active issue for this block, computed once — read by this bar (top-priority
    // one only) and, once the test panel lands, its diagnostics list too, so the surfaces can never
    // disagree about wording (see keywordIssues' own doc comment).
    const issues = settleStored(memoizedByKey(keywordIssuesCache, block.data, [variables, t, shownVersionId], () => keywordIssues(d, issueCtx)));
    const issueBar = collapsedIssueBar(issues);
    const barNode = (() => {
      if (issueBar) return issueBar;
      if (isMain) return null;
      return collapsedModeBar(d.gradingMode, t, 'keyword');
    })();

    return (
      <CollapsedInputBlock
        block={block}
        blockRef={blockRef}
        contentRef={collapsedContentRef}
        icon={<TextCursor className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
        titleLabel={t('blocks.keyword.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={displayMode.label}
        GradingModeIcon={displayMode.Icon}
        gradingModeChipClass={dispChipClass}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={keywordAriaLabel}
        answerBarSlot={() => barNode}
        testEvaluation={testResult ? (testResult[0]?.evaluation ?? 'neutral') : undefined}
        testBar={isSelected ? {
          continuous,
          onContinuousChange: handleContinuousChange,
          onSubmit: handleTestSubmit,
          hasResult: hasSubmitted,
          chips: testChips,
          onReset: handleTestReset,
          // Versions are rollable too: a banked block can be rerolled with no chips in it.
          onReroll: (hasChips && hasRollable) ? handleTestReroll : undefined,
          // Which branch resolved for this test, with a one-click jump straight to editing it —
          // deliberately NOT automatic on every reroll (see pendingBranchJump's doc comment).
          /* One row for everything this test resolved to — the version it drew and the branch
             that matched. Shared with the phone bar, MCQ and Dropdown: see
             `version-branch-notice.tsx` for the layout and the rule that a scope tag is never
             truncated. */
          topNotices: (
            <VersionBranchNotice
              versions={scenario.testPager?.versions ?? shownVersions}
              activeVersionId={scenario.testPager ? scenario.testPager.activeId : shownVersionId}
              onSelectVersion={scenario.testPager?.onSelect ?? setTestVersion}
              noun={scenario.testPager?.noun ?? 'version'}
              editScrollsTo={scenario.activeScenario?.scenarioId ? { fieldId: scenario.activeScenario?.scenarioId, blockId: block.id } : undefined}
              stacked={isPhone}
              branch={matchedBranch && matchedBranchIndex !== -1 ? {
                index: matchedBranchIndex,
                total: d.conditionalBranches?.length ?? 0,
                equation: d.conditionalEquation ?? '',
                rolledValues,
                value: matchedEquationValue,
                operator: matchedBranch.operator,
                threshold: matchedBranch.threshold,
                thresholdHigh: matchedBranch.thresholdHigh,
              } : null}
              onEdit={() => {
                if (matchedBranch && matchedBranchIndex !== -1) requestBranchJump(block.id, matchedBranchIndex);
                editShownVersion(matchedBranch?.id);
                if (onSelect) onSelect();
                else { shouldFocusEditor.current = true; setIsEditing(true); }
              }}
            />
          ),
          notices: (
            <IssuesPanel issues={issues} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
          ),
        } : undefined}
        onActivate={() => {
          if (onSelect) { onSelect(); }
          else { shouldFocusEditor.current = true; setIsEditing(true); }
        }}
      >
        {isSelected ? (
          <KeywordAnswerDisplay
          scenario={scenario.ghostedScenario}
            question={d.question}
            rolledValues={rolledValues}
            value={testAnswer}
            onChange={handleTestChange}
          >
            {renderKeywordFiredFeedback(testResult, rolledValues)}
          </KeywordAnswerDisplay>
        ) : (
          <>
            <BlockQuestion html={d.question} spaced showEmptyNote />
            <div className='mx-auto flex min-h-9 w-full items-start rounded-md border border-ws-border-strong bg-ws-btn px-3 py-2 md:max-w-xs'>
              {correctKeywordsText ? (
                <span className='flex-1 break-words font-mono text-sm text-ws-text'>{correctKeywordsText}</span>
              ) : (
                <span className='flex-1' />
              )}
            </div>
            {keywordNoteParts.length > 0 && (
              <p className='mt-1.5 text-center text-xs text-ws-muted'>{keywordNoteParts.join(', ')}</p>
            )}
          </>
        )}
      </CollapsedInputBlock>
    );
  }

  // ── Feedback slots ──────────────────────────────────────────────────────────

  const unansweredState = activeFeedbackStates.find(s => s.trigger === 'unanswered');
  const noAnswerSlot = unansweredState ? (() => {
    const s = unansweredState;
    const ORDER = ['neutral', 'incorrect'] as const;
    return (
      <FeedbackCard fieldId={s.id}
        title={getTitle(s)} content={s.content} evaluation={s.evaluation}
        locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.noAnswerTitle')}
        onTitleChange={() => {}} onDelete={() => {}}
        onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
        onEvaluationCycle={() => {
          const cur = s.evaluation === 'correct' ? 'neutral' : s.evaluation as 'neutral' | 'incorrect';
          updateActiveFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length]! });
        }}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    );
  })() : null;

  // Mode-scoped: optional is INERT and shows no targeted state at all, and `set_answer` is
  // hidden in graded where Correct already says it. Hidden, never deleted — see `targetedFiresIn`.
  const targetedStates = visibleTargeted(activeFeedbackStates, gradingMode);
  const usedTriggers = new Set(targetedStates.map(s => s.trigger));
  // Outside graded every answer can carry a targeted state (nothing is correct, so nothing is
  // terminal), plus the blank in optional — see addTargetedState for why a blank is in this list.
  const canAddMore = effectiveOptionsForActive.some(o =>
    (gradingMode !== 'graded' || o.role !== 'correct') && !usedTriggers.has(`option_${o.id}`));
  const visibleTargetedStates = targetedStates.filter(s => !isTargetedStaleForActive(s));

  const targetedSlot = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleFeedbackTargetedDragEnd}
      accessibility={dndA11y(t, visibleTargetedStates.map(x => x.id), 'feedback')}
    >
      <SortableContext items={visibleTargetedStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-3'>
          {visibleTargetedStates.map(s => {
            const usedByOthers = new Set(
              activeFeedbackStates.filter(st => st.type === 'targeted' && st.id !== s.id).map(st => st.trigger)
            );
            const triggerOptions: TriggerOption[] = effectiveOptionsForActive.flatMap((opt, i) => {
              if (opt.role === 'correct' || usedByOthers.has(`option_${opt.id}`)) return [];
              return [{
                value: `option_${opt.id}`,
                label: opt.text || t('blocks.keyword.optionLabel', { number: i + 1 }),
                text: opt.text,
                evaluation: (opt.role === 'incorrect' ? 'incorrect' : 'neutral') as TriggerOption['evaluation'],
              }];
            });
            const currentOpt = triggerOptions.find(o => o.value === s.trigger) ?? triggerOptions[0];
            return (
              <SortableFeedbackCard
                key={s.id}
                sortId={s.id}
                title={s.title} content={s.content} evaluation={displayEvaluation(gradingMode, s.evaluation)}
 /* Collapse on a grading-mode switch: these cards do not remount, so one the teacher
    had collapsed sprang back open every time they flipped modes to compare. */
 collapseSignal={gradingMode}
                startExpanded={justAddedKey(s.id) === justAddedStateId}
                titleLocked
                evalLocked
                triggerZone={<TriggerDropdown value={s.trigger} options={triggerOptions} onChange={trigger => {
                  const newId = trigger.slice('option_'.length);
                  const idx = effectiveOptionsForActive.findIndex(o => o.id === newId);
                  const opt = idx === -1 ? undefined : effectiveOptionsForActive[idx];
                  updateActiveFeedbackState(s.id, {
                    trigger,
                    title: opt ? keywordTargetedTitle(opt, t) : s.title,
                    evaluation: opt ? (opt.role === 'neutral' ? 'neutral' : 'incorrect') : s.evaluation,
                  });
                }} />}
                triggerZoneSummary={currentOpt ? <KeywordTriggerChip option={currentOpt} /> : undefined}
                onTitleChange={() => {}}
                onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
                onEvaluationCycle={() => {}}
                onDelete={() => deleteActiveFeedbackState(s.id)}
                activeEditorRef={activeEditorRef}
                setActiveEditor={setActiveEditor}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );

  const activeOptionRoleById = new Map(effectiveOptionsForActive.map(o => [o.id, o.role]));
  const coveredIds = new Set(
    activeFeedbackStates
      .filter(s => s.type === 'targeted' && s.trigger.startsWith('option_'))
      .map(s => s.trigger.slice('option_'.length))
      // Exclude stale feedback (target keyword since marked correct) — otherwise a
      // correct keyword with leftover feedback would wrongly appear "covered" and
      // drop out of the Default Correct list even though that feedback is inactive.
      .filter(id => activeOptionRoleById.get(id) !== 'correct')
  );
  const uncoveredIncorrect = effectiveOptionsForActive.filter(opt => opt.role !== 'correct' && !coveredIds.has(opt.id));
  // Outside graded nothing is correct, so nothing is terminal and EVERY untargeted word still
  // reaches the catch-all — not only the non-correct ones.
  // Same derivation the test bar reads.
  const panelReach = keywordReachability({ gradingMode, feedback: { states: activeFeedbackStates } }, effectiveOptionsForActive);
  const uncoveredCorrect  = panelReach.answerable.filter(opt => !coveredIds.has(opt.id));

  // The same lists the test legend draws — see `keywordChips`. The bespoke `<span>`s these replaced
  // were a second copy of one condition, and the two were already inconsistent about chip width and
  // about whether a chip used shared tone classes or literal ones.
  const chipRow = (chips: ConditionChips) => (
    <ConditionChipRow chips={chips} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />
  );
  const defaultIncorrectInline = chipRow(keywordChips('incorrect', uncoveredIncorrect, t));
  const defaultCorrectInline = chipRow(keywordChips('correct', uncoveredCorrect, t));

  /**
   * The catch-all names what it still catches, as Default Incorrect does — but keyword's answer
   * space is OPEN, so "any unlisted word" stays in the row whatever is listed beside it. A
   * student can always type something nobody targeted, which is why this card can never close.
   */
  const participationCatchAllInline = chipRow(catchAllChips(gradingMode, visibleTargetedStates.length > 0, t,
    () => keywordChips('incorrect', uncoveredIncorrect, t, gradingMode)));
  const anyAnswerInline = participationCatchAllInline;
  const anyResponseInline = participationCatchAllInline;
  const positiveTriggerZone =
    gradingMode === 'survey' ? anyAnswerInline :
    gradingMode === 'optional' ? anyResponseInline :
    defaultCorrectInline;

  const correctSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && (s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'))
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked
        inactive={gradingMode === 'graded' && uncoveredCorrect.length === 0}
        triggerZone={positiveTriggerZone}
        triggerZoneSummary={positiveTriggerZone}
        title={catchAllTitle(gradingMode, visibleTargetedStates.length > 0, t) ?? getTitle(state)}
        content={state.content}
        /**
         * The tick goes the moment a target exists — the catch-all must not affirm an answer the
         * teacher ignored more strongly than one they wrote for. With no targets the block is
         * still a plain questionnaire and keeps it. Same rule `catchAllChips` applies to the chip
         * beside it, so the card and its trigger cannot disagree.
         */
        evaluation={gradingMode !== 'graded' && visibleTargetedStates.length > 0 ? 'neutral' : state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  // incorrectSlot never goes inactive — free text always has an "any unlisted word"
  // catch-all, so Default Incorrect can never be fully covered the way Correct can.
  const incorrectSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && s.trigger === 'incorrect')
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked
        triggerZone={defaultIncorrectInline}
        triggerZoneSummary={defaultIncorrectInline}
        title={getTitle(state)}
        content={state.content}
        evaluation={state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  // ── Live in-editor issues ────────────────────────────────────────────────────
  // The exact same computation the collapsed bar (and, once the test panel lands, its own
  // diagnostics list) uses, run against the editor's own live state instead of
  // `initData(block.data)` — see dropdownIssues'/keywordIssues' own doc comments.
  /* Enumerated, so a field left out is a check that silently never runs — `scenarioBranches`
     is what makes a follower's live test grade against its row rather than its own key. */
  const liveD: KeywordBlockData = { question: questionHtmlRef.current, options, caseSensitive, accentSensitive, gradingMode, feedback, conditionalEquation, conditionalBranches, angleMode, followsBlockId: initData(block.data).followsBlockId, scenarioBranches: scenario.rows };
  const editorIssues = settleLive(keywordIssues(liveD, issueCtx));
  // ONE derivation, read from the issues themselves rather than re-running the duplicate check to
  // decide which rows to ring — the pattern `BlockIssue.fields` exists for, already used by
  // dropdown/mcq/ordering. The duplicate check has always populated `fields`; nothing consumed it
  // here, so the panel named a collision and no field showed which keywords it meant.
  const collidingOptionIds = issueFieldIds(editorIssues);
  // missing-feedback-text is excluded because it already has its own more specific home (each
  // blank feedback card shows its own warning directly) — see dropdownIssues' identical
  // exclusion for the full reasoning.

  // ── Phone "Test" tab ─────────────────────────────────────────────────────────
  // Only ever rendered while `isPhone` (InputBlockFrame ignores it otherwise), and only replaces
  // the editingSlot content — mirrors DropdownBlock's own identical phone Test tab wiring, fed
  // `liveD` instead of `initData(block.data)` so it always reflects the editor's own live state.
  const phoneTest = isPhone
    ? computeKeywordTestData(liveD, variables, t, rolledValues, submittedAnswer, hasSubmitted, scenario.activeScenario?.scenarioId)
    : null;
  const phoneHasChipsRollable = phoneTest ? (phoneTest.hasChips && phoneTest.hasRollable) : false;
  const phoneTestSlot = phoneTest ? (
    <KeywordAnswerDisplay
          scenario={scenario.ghostedScenario}
      question={liveD.question}
      rolledValues={rolledValues}
      value={testAnswer}
      onChange={handleTestChange}
    >
      {renderKeywordFiredFeedback(phoneTest.testResult, rolledValues)}
    </KeywordAnswerDisplay>
  ) : null;
  const phoneTestBar: TestBarProps | undefined = phoneTest ? {
    continuous,
    onContinuousChange: handleContinuousChange,
    onSubmit: handleTestSubmit,
    hasResult: hasSubmitted,
    chips: phoneTest.testChips,
    onReset: handleTestReset,
    onReroll: phoneHasChipsRollable ? handleTestReroll : undefined,
    /* The same row as the canvas card's — one component, so the two can never drift. */
    topNotices: (
      <VersionBranchNotice
        versions={scenario.testPager?.versions ?? shownVersions}
        activeVersionId={scenario.testPager ? scenario.testPager.activeId : shownVersionId}
        /* The EDITOR's pointer here: on a phone, Edit and Test are two tabs over ONE editing
           session and this tab renders the editor's live fields. */
        onSelectVersion={scenario.testPager?.onSelect ?? selectVersion}
        noun={scenario.testPager?.noun ?? 'version'}
        editScrollsTo={scenario.activeScenario?.scenarioId ? { fieldId: scenario.activeScenario?.scenarioId, blockId: block.id } : undefined}
        stacked
        branch={phoneTest.matchedBranch && phoneTest.matchedBranchIndex !== -1 ? {
          index: phoneTest.matchedBranchIndex,
          total: conditionalBranches?.length ?? 0,
          equation: conditionalEquation ?? '',
          rolledValues,
          value: phoneTest.matchedEquationValue,
          operator: phoneTest.matchedBranch.operator,
          threshold: phoneTest.matchedBranch.threshold,
          thresholdHigh: phoneTest.matchedBranch.thresholdHigh,
        } : null}
        onEdit={() => {
          if (phoneTest.matchedBranch && phoneTest.matchedBranchIndex !== -1) {
            setActiveBranchIndex(phoneTest.matchedBranchIndex);
          }
          editShownVersion(phoneTest.matchedBranch?.id);
          setPhoneTestMode('edit');
        }}
      />
    ),
    notices: (
      <IssuesPanel issues={editorIssues} phase='editing' className='rounded-md border border-ws-border px-3 py-2' />
    ),
  } : undefined;

  // ── Feedback branch scroller ──────────────────────────────────────────────────
  // Same shared activeBranchIndex the setup carousel below uses, in a separate control here so
  // switching branches can never accidentally leave the two views out of sync.
  // Graded-only, like every other branching surface (activeBranch, showConditionalUI): branches are
  // read straight from persisted data, so a block authored with branches and later switched to
  // Participation/Optional kept showing this scroller above states those branches can never reach.
  const feedbackBranchScroller = (
    /* Six blocks had written this identically; see `FeedbackBranchPager`. */
    <FeedbackBranchPager
      gradingMode={gradingMode}
      branches={conditionalBranches}
      activeIndex={activeBranchIndex}
      onActiveIndexChange={setActiveBranchIndex}
    />
  );

  // ── Editing shell ───────────────────────────────────────────────────────────

  return (
    <InputBlockFrame
      {...angleModeProps}
      blockId={block.id}
      fullScreen={isPhone && !embedded}
      landscapeLayout={isPhoneLandscape}
      icon={<TextCursor className='h-6 w-6' strokeWidth={2} />}
      titleLabel={t('blocks.keyword.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.keyword.closeEditor')}
      onClose={handleDone}
      gradingMode={gradingMode}
      gradingModeLabel={currentMode.label}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: currentMode.label, next: nextMode.label })}
      onGradingModeCycle={() => handleGradingMode(nextMode.id)}
      testSlot={phoneTestSlot}
      testBar={phoneTestBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      warningBarSlot={editorIssues.length > 0 ? (
        <IssuesPanel issues={editorIssues} phase='editing' className='px-4 py-2.5' />
      ) : undefined}
      settingsSlot={
        <>
          <OptionsSection title={t('blocks.optionsSections.answerFormat')}>
            <CheckboxRow fieldId='caseSensitive'
              // Every option this block has is a MATCHING rule, and optional matches nothing —
              // it accepts whatever arrives. Disabled in place: a lone control, not a section.
              disabled={!settingAvailable('caseSensitive', gradingMode)}
              checked={caseSensitive}
              onToggle={() => handleCaseSensitive(!caseSensitive)}
              label={t('blocks.matching.caseSensitive')}
            />
            <CheckboxRow fieldId='accentSensitive'
              // Every option this block has is a MATCHING rule, and optional matches nothing —
              // it accepts whatever arrives. Disabled in place: a lone control, not a section.
              disabled={!settingAvailable('accentSensitive', gradingMode)}
              checked={accentSensitive}
              onToggle={() => handleAccentSensitive(!accentSensitive)}
              label={t('blocks.matching.accentSensitive')}
            />
          </OptionsSection>
        </>
      }
      settingsA11yLabel={t('blocks.keyword.optionsDrawerToggle')}
      settingsLabel={t('common.options')}
      feedbackLabel={t('blocks.areas.feedback')}
      defaultIsEditingFeedback={initialIsEditingFeedback}
      onFeedbackModeChange={onFeedbackModeChange}
      sharedToolbar={sharedToolbar}
      feedbackPanelProps={{
        beforeSlot: scenario.feedbackScroller ?? feedbackBranchScroller,
        noAnswerSlot,
        correctSlot,
        targetedSections: [{
          key: 'targeted',
          heading: t('blocks.feedbackPanel.targeted'),
          content: targetedSlot,
          canAddMore,
          addLabel: t('blocks.feedbackPanel.add'),
          onAdd: addTargetedState,
          isEmpty: visibleTargetedStates.length === 0,
        }],
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot,
      }}
      editingSlot={
          <div className='flex flex-col gap-4 px-6 py-4'>
          {/* A bank is a niche feature: given its own row it was the first thing the eye landed
              on. In the heading it is available without being advertised, and the heading matches
              ANSWER SETUP below so the editor gains a section rather than a widget. */}
          {/* The three-way offer, when linking meets an existing bank — §7.1. */}
          {scenario.remapModal}
          {/* The link control and what it points at, for BOTH kinds of link — see
              `VariesWithSection`, which decides on its own whether a DRIVEN block
              needs it. This was five lines of identical JSX in twelve blocks. */}
          <VariesWithSection
            following={!!scenario.activeScenario}
            linkMenu={scenario.linkMenu}
            stimulus={scenario.editorScenario}
          />
          {/* EVERYTHING THE ROW OWNS, inside the row's own box — the question as well as
              the answer setup, because a driven axis varies the question too. Renders its
              children bare when this block is not driven. See `DrivenRowFrame`. */}
          <DrivenRowFrame block={block} data={initData(block.data)} emit={emit}>
          <EditorSectionHeading
            label={t('blocks.areas.question')}
            /* First heading in the panel unless a scenario is above it. */
            divider={!!scenario.activeScenario}
            action={(
              <div className='flex items-center gap-2'>
              {/* The bank is GONE while this block follows a scenario, not merely inert (§2.2):
                  versions are many stems each with one answer setup, a scenario is one stem
                  with many, and the two cannot both be the answer key. */}
              {!scenario.following && (
              <VersionSelector
                versions={(initData(block.data).versions ?? []) as { id: string }[]}
                activeId={editorVersionId}
                onSelect={selectVersion}
                onAdd={() => {
                  const next = addVersion(initData(block.data), editorVersion, () => crypto.randomUUID());
                  emit(versionStructure(block.data, next));
                  // Land on the one just made: "+" means write a DIFFERENT question.
                  const added = next.versions?.[next.versions.length - 1];
                  if (added) selectVersion(added.id, next);
                }}
                onRemove={(id) => {
                  const next = removeVersion(initData(block.data), id);
                  emit(versionStructure(block.data, next));
                  const landing = next.versions?.[0];
                  selectVersion(landing?.id, next);
                  setTestVersion(landing?.id);
                }}
              />
              )}
              {/* Silent once there is a Varies-With section, which is where the
                  control moves to — see `QuestionHeadingLink`. Written as
                  `!activeScenario` it knew about scenarios only, so a DRIVEN block
                  showed the link twice and the heading overflowed sideways. */}
              <QuestionHeadingLink
                following={!!scenario.activeScenario}
                linkMenu={scenario.linkMenu}
              />
              </div>
            )}
          />
          <BlockQuestionEditor
            value={questionHtml}
            onChange={html => { questionHtmlRef.current = html; setQuestionHtml(html); push(); }}
            toolbar={sharedToolbar}
            editorRef={questionEditorRef}
            shouldFocusRef={shouldFocusEditor}
            embedded={embedded}
          />

          {/*
            * ANSWER SETUP, hidden in OPTIONAL only.
            *
            * Optional is the one mode with no use for an answer key — it accepts whatever arrives
            * and records it. Survey keeps the list: a targeted state can speak to a particular
            * word there, so the words are live even though nothing is graded.
            *
            * Hidden rather than greyed, because the whole SECTION's job is gone — the same call
            * the feedback panel already makes for its correctness cards. Nothing is deleted:
            * switch back and every word is as it was.
            */}
          {gradingMode !== 'optional' && (<>
            <EditorSectionHeading label={t('blocks.areas.answerSetup')} />

            {/* No mode gate. The branch UI inside is already graded-only on its own terms; this
                outer ternary additionally hid the word list, and replaced it with "Any inputted word
                will be accepted" — which stopped being true the moment a word could carry targeted
                feedback and split a pathway. */}
            {(() => {
                  // A variable's presence in the question is what makes branch-based correctness
                  // meaningful at all — keyword text is a plain string, so it can never itself hold
                  // a variable chip (see the port plan), unlike Dropdown/MCQ's own `hasVariable`
                  // check which also inspects option text/formulas.
                  const hasVariable = htmlHasVariableChips(questionHtmlRef.current);
                  /* FOLLOWING NO LONGER RULES BRANCHING OUT — when both are on the branching nests
                     INSIDE the scenario row. See `use-nested-branching`. */
                  const showConditionalUI = hasVariable && gradingMode === 'graded';
                  const hasTrigAnywhere = TRIG_CALL_PATTERN.test(conditionalEquation ?? '');
                  const isEnabled = conditionalBranches !== undefined;
                  /* WHOSE branch set is on screen — the row's when following, the block's otherwise. */
                  const { nesting, effEquation, effEnabled, effBranches, effIndex, effActiveBranch } =
                    nestedBranching<KeywordBlockData['feedback']>({
                      activeScenario: scenario.activeScenario,
                      blockEquation: conditionalEquation,
                      blockBranches: conditionalBranches ?? [],
                      blockEnabled: isEnabled,
                      activeBranchIndex,
                      graded: gradingMode === 'graded',
                    });
                  const makeRow = (mode: SeedMode) => makeRowBranch<KeywordBlockData['feedback']>({
                    mode,
                    roles: (scenario.activeScenario as { roles?: Record<string, unknown> } | undefined)?.roles ?? {},
                    feedback: (scenario.activeScenario as unknown as { feedback: KeywordBlockData['feedback'] }).feedback,
                    cloneFeedback: cloneFeedbackWithFreshIds,
                    seedFeedback: seedBranchFeedback,
                  });
                  /** One toggle, two scopes — the ROW's while following, the block's otherwise. */
                  const toggleRowBranching = () => {
                    if (!nesting) { toggleConditionalCorrectness(!isEnabled); return; }
                    setActiveBranchIndex(0);
                    scenario.writeActiveRow((effEnabled
                      ? { conditionalEquation: undefined, conditionalBranches: undefined }
                      : { conditionalEquation: effEquation ?? '',
                          conditionalBranches: [makeRow('migrate')] }) as never);
                  };
                  /*
                   * WHERE THE TOGGLE SITS IS A CLAIM ABOUT WHAT IT APPLIES TO. On a follower it turns
                   * branching on for ONE ROW, so standing above the scenario box reads as "every scenario".
                   */
                  const branchingToggle = showConditionalUI ? (
                    <BranchingCorrectsRow
                      checked={effEnabled}
                      onToggle={toggleRowBranching}
                    />
                  ) : null;
                  const blockLevelToggle = showConditionalUI && !nesting;
                  const branches = conditionalBranches ?? [];
                  const clampedIndex = Math.min(activeBranchIndex, Math.max(0, branches.length - 1));
                  // Gated on gradingMode, not just isEnabled — same reasoning as Dropdown's own
                  // identical shadowing local (a stale branch's roles must never leak into the
                  // options-list editor's role display once grading mode is switched away).
                  const activeBranchLocal = isEnabled && gradingMode === 'graded' ? branches[clampedIndex] : undefined;
                  const activeRow = scenario.activeScenario ?? activeBranchLocal;
                  const hasEquation = !!conditionalEquation?.trim();
                  // minSampleValues, NOT `rolledValues` — that state stays {} until the block is
                  // actually isSelected, which would make every equation referencing a variable
                  // look invalid the entire time it's being authored.
                  const equationInvalid = hasEquation && evaluateEquation(conditionalEquation!, minSampleValues(variables), angleMode) === null;
                  const equationWorks = hasEquation && !equationInvalid;

    // Rendered WITH the list in both states, the pattern Scale already used. Adding an option is an
    // answer-set operation, so the control belongs with the answer set — and when branching is on the
    // answer set lives inside the carousel, which is where the button sits rather than across a
    // border from the list it appends to.
    //
    // It is global, not per-branch: an option added here appears in every condition. That reads as a
    // scope claim only if you believe the carousel bounds a scope, and it does not — the option text,
    // formula and media edited inside it are equally global, and only the role toggle varies.
    const addOptionButton = (
              <AddRowButton
                ref={addOptionButtonRef}
                label={t('blocks.keyword.addOption')}
                onClick={addOption}
              />
    );

                  const optionListJsx = (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleOptionDragEnd}
                      accessibility={dndA11y(t, options.map(x => x.id), 'keyword')}
      >
                      <SortableContext items={options.map(o => o.id)} strategy={verticalListSortingStrategy}>
                        <div className='flex flex-col gap-3'>
                          {options.map(opt => (
                            <SortableOptionRow
                              key={opt.id}
                              /* The row the list edits: a scenario first, since a linked block
                                 has no numeric branch to fall through to. */
                              opt={{ ...opt, role: roleForMode(gradingMode, activeRow ? activeRow.roles[opt.id] ?? 'neutral' : opt.role) }}
                              optionCount={options.length}
                              roleLocked={gradingMode !== 'graded'}
                              armedOptionId={armedOptionId}
                              setArmedOptionId={setArmedOptionId}
                              onRoleClick={scenario.activeScenario ? handleScenarioRoleClick
                                : activeBranchLocal ? (id) => handleBranchRoleClick(activeBranchLocal.id, id) : handleRoleClick}
                              onTextChange={updateOptionText}
                              onRemove={removeOption}
                              activeEditorRef={activeEditorRef}
                              setActiveEditor={setActiveEditor}
                              hasDuplicate={collidingOptionIds.has(opt.id)}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  );

                  return (
                    <>
                      {/* Guarded as a whole, not just child by child: every child here is conditional, so an
                          unguarded wrapper became an empty flex item whose parent gap applied both above and
                          below it — a doubled gap under the Answer Setup rule whenever no variable was in
                          play. Matches Ordering/FITB/Scale/GPS, which already guard theirs. */}
                      {(showConditionalUI || hasTrigAnywhere) && (
                        <div className='flex flex-col gap-2 pl-1'>
                          {blockLevelToggle && branchingToggle}

                        </div>
                      )}

                      {showConditionalUI && isEnabled && activeBranchLocal ? (
                        <BranchConditionCarousel
                        seedVariable={(() => { const n = variableChipNames(questionHtmlRef.current); return n.length === 1 ? n[0] : undefined; })()}
                          ref={branchPrevButtonRef}
                          equation={conditionalEquation ?? ''}
                          onEquationChange={updateConditionalEquation}
                          variables={variables}
                          rolledValues={rolledValues}
                          angleMode={angleMode}
                          equationInvalid={equationInvalid}
                          equationWorks={equationWorks}
                          hasError={editorIssues.some(i => i.severity === 'error' && ['no-equation', 'invalid-equation', 'coverage-gap'].includes(i.key))}
                          branches={branches}
                          activeIndex={clampedIndex}
                          onActiveIndexChange={setActiveBranchIndex}
                          onBranchChange={updateConditionalBranch}
                          onAdd={addConditionalBranch}
                          onRemove={removeConditionalBranch}
                          onFieldFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                        >
                          {optionListJsx}
                          {addOptionButton}
                        </BranchConditionCarousel>
                      ) : (
                        /* The scenario's own box — the carousel's shape with a variant in place
                           of an equation, and nothing at all when this block follows nothing. */
                        <ScenarioSetupFrame
                          scenario={scenario.activeScenario}
                          name={(scenario.activeScenarioVersion as { name?: string } | null)?.name}
                          pager={scenario.pager}
                          unreviewedCount={scenario.unreviewedCount}
                          onDismissUnreviewed={scenario.dismissUnreviewed}
                        >
                          {/* Only when there IS a box: the frame passes children through unwrapped when this
                              block follows nothing, so an ungated toggle renders a SECOND copy here. */}
                          {nesting && branchingToggle && <div className='pb-2'>{branchingToggle}</div>}
                          {showConditionalUI && effEnabled && effActiveBranch ? (
                            <BranchConditionCarousel
                              seedVariable={(() => { const n = variableChipNames(questionHtmlRef.current); return n.length === 1 ? n[0] : undefined; })()}
                              equation={effEquation ?? ''}
                              onEquationChange={v => scenario.writeActiveRow({ conditionalEquation: v } as never)}
                              variables={variables}
                              rolledValues={rolledValues}
                              angleMode={angleMode}
                              equationInvalid={false}
                              equationWorks={!!effEquation?.trim()}
                              hasError={false}
                              branches={effBranches}
                              activeIndex={effIndex}
                              onActiveIndexChange={setActiveBranchIndex}
                              onBranchChange={(id, patch) => scenario.writeActiveRow({
                                conditionalBranches: effBranches.map(b => (b.id === id ? { ...b, ...patch } : b)) } as never)}
                              onAdd={() => scenario.writeActiveRow({
                                conditionalBranches: [...effBranches, makeRow('fork')] } as never)}
                              onRemove={id => scenario.writeActiveRow({
                                conditionalBranches: effBranches.filter(b => b.id !== id) } as never)}
                              onFieldFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                            >
                          {optionListJsx}
                          {addOptionButton}
                            </BranchConditionCarousel>
                          ) : (<>
                          {optionListJsx}
                          {addOptionButton}
                          </>)}
                        </ScenarioSetupFrame>
                      )}
                    </>
                  );
            })()}
          </>)}

          </DrivenRowFrame>
          </div>
      }
    />
  );
}
